import { useEffect, useRef, useState } from "react";
import { ColorPicker } from "./components/ColorPicker";
import {
  CARD_COLORS,
  CARD_COLOR_LABELS,
  CARD_COLOR_VAR,
  RAMP_STEPS,
  colors,
  motion,
  ramps,
  type CardColor,
  type Ramp,
  type RampName,
  type RampStep,
} from "./tokens";

const SECTIONS: { id: string; label: string }[] = [
  { id: "foundations", label: "Foundations" },
  { id: "typography", label: "Typography" },
  { id: "color", label: "Color" },
  { id: "spacing", label: "Spacing" },
  { id: "corners", label: "Corners" },
  { id: "motion", label: "Motion" },
  { id: "buttons", label: "Buttons" },
  { id: "pills", label: "Pills & Toggles" },
  { id: "inputs", label: "Inputs" },
  { id: "form-controls", label: "Form Controls" },
  { id: "chips", label: "Chips & Tags" },
  { id: "tabs", label: "Standard Tabs" },
  { id: "cards", label: "Cards" },
  { id: "drawers", label: "Drawers" },
  { id: "tables", label: "Tables" },
  { id: "dates", label: "Dates" },
  { id: "calendar", label: "Calendar" },
  { id: "drag-sort", label: "Drag & Sort" },
  { id: "tooltips", label: "Tooltips" },
  { id: "toasts", label: "Toasts" },
  { id: "avatars", label: "Avatars" },
  { id: "navigation", label: "Navigation" },
  { id: "charts", label: "Charts" },
  { id: "icons", label: "Icons" },
  { id: "logos", label: "Logos" },
  { id: "color-picker", label: "Color Picker" },
  { id: "layouts", label: "Layouts" },
];

const VERSION = "v0.3.6";

export function BrandSystem() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  // Reset to top on load so the active TOC item matches what's at the top.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    window.scrollTo(0, 0);
  }, []);

  // Highlight the section currently nearest the top.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 },
    );
    for (const s of SECTIONS) {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[color:var(--color-app)] text-[color:var(--color-body)] antialiased">
      <header
        className="sticky top-0 z-30 backdrop-blur"
        style={{
          background: "color-mix(in srgb, var(--color-app) 92%, transparent)",
          borderBottom: "1px solid var(--color-rule)",
        }}
      >
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            <img src="/sphere-on-light.png" alt="Sphere" style={{ height: 26, width: "auto" }} />
            <span style={{ width: 1, height: 18, background: "var(--color-rule)" }} />
            <span className="text-[15px] font-semibold tracking-tight text-[color:var(--color-ink)]">
              Design System
            </span>
            <span className="text-[11px] text-[color:var(--color-ink-muted)]">
              {VERSION}
            </span>
          </div>
          <a
            href="https://github.com/dougburnett/sphere-design-system"
            target="_blank"
            rel="noreferrer"
            className="text-[12px] text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]"
          >
            GitHub ↗
          </a>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1200px] gap-12 px-6">
        <Toc active={activeId} />
        <main className="min-w-0 flex-1 space-y-24 py-12" style={{ paddingBottom: "40vh" }}>
          <SectionFoundations />
          <SectionTypography />
          <SectionColor />
          <SectionSpacing />
          <SectionCorners />
          <SectionMotion />
          <SectionButtons />
          <SectionPills />
          <SectionInputs />
          <SectionFormControls />
          <SectionChips />
          <SectionTabs />
          <SectionCards />
          <SectionDrawers />
          <SectionTables />
          <SectionDates />
          <SectionCalendar />
          <SectionDragSort />
          <SectionTooltips />
          <SectionToasts />
          <SectionAvatars />
          <SectionNavigation />
          <SectionCharts />
          <SectionIcons />
          <SectionLogos />
          <SectionColorPicker />
          <SectionLayouts />
          <Footer />
        </main>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function Toc({ active }: { active: string }) {
  return (
    <aside className="sticky top-20 hidden h-[calc(100vh-5rem)] w-44 shrink-0 pt-12 md:block">
      <ul className="space-y-0.5">
        {SECTIONS.map((s) => {
          const isActive = active === s.id;
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                className="block rounded px-3 py-1.5 text-[12.5px] transition"
                style={{
                  background: isActive ? "var(--color-page)" : "transparent",
                  color: isActive ? "var(--color-ink)" : "var(--color-ink-muted)",
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {s.label}
              </a>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function Section({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-[26px] font-semibold tracking-tight text-[color:var(--color-ink)]">
        {title}
      </h2>
      {description && (
        <p className="mt-1.5 max-w-[60ch] text-[13px] text-[color:var(--color-body-muted)]">
          {description}
        </p>
      )}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Specimen({
  label,
  children,
  className,
}: {
  label?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        border: "1px solid var(--color-rule)",
        borderRadius: 8,
        background: "var(--color-surface)",
      }}
    >
      {label && (
        <div
          className="border-b px-4 py-2 text-[11px] uppercase tracking-[0.14em]"
          style={{
            borderColor: "var(--color-rule)",
            color: "var(--color-ink-muted)",
          }}
        >
          {label}
        </div>
      )}
      <div className="p-6">{children}</div>
    </div>
  );
}

// ─── Sections ───────────────────────────────────────────────────────────────

function SectionFoundations() {
  return (
    <Section
      id="foundations"
      title="Foundations"
      description="Tokens, fonts, and motion that every Sphere product is built on. Designed to be consumed as CSS variables or JS exports — never hard-coded hexes in product code."
    >
      <ul className="list-disc space-y-1.5 pl-6 text-[13px] text-[color:var(--color-body)]">
        <li>
          <strong className="font-semibold text-[color:var(--color-ink)]">Type:</strong>{" "}
          Söhne Buch (400) for body, Söhne Halbfett (600) for headings + emphasis.
        </li>
        <li>
          <strong className="font-semibold text-[color:var(--color-ink)]">Color:</strong>{" "}
          Cream / navy core, with sunshine / lavender / tope accents. Semantic
          tokens (<code className="font-mono text-[12px]">--color-ink</code>,
          {" "}
          <code className="font-mono text-[12px]">--color-page</code>, …) resolve
          to the brand palette below.
        </li>
        <li>
          <strong className="font-semibold text-[color:var(--color-ink)]">Motion:</strong>{" "}
          250 ms <code className="font-mono text-[12px]">cubic-bezier(0.645, 0.045, 0.355, 1)</code>{" "}
          (ease-in-out) for drawer slides; 250 ms cubic ease-out for in-place pushes.
        </li>
        <li>
          <strong className="font-semibold text-[color:var(--color-ink)]">Surfaces:</strong>{" "}
          App chrome on <code className="font-mono text-[12px]">--color-app</code> (#FAF6F0),
          document surface on <code className="font-mono text-[12px]">--color-beige</code> (#FFFCF6),
          drawer on <code className="font-mono text-[12px]">--color-drawer</code> (#FAFAF7).
        </li>
      </ul>
    </Section>
  );
}

function SectionTypography() {
  const samples: Array<{ name: string; size: number; weight: 400 | 600; lh: number }> = [
    { name: "Display", size: 34, weight: 600, lh: 40 },
    { name: "Title 1", size: 26, weight: 600, lh: 32 },
    { name: "Title 2", size: 22, weight: 600, lh: 28 },
    { name: "Title 3", size: 18, weight: 600, lh: 24 },
    { name: "Body", size: 14, weight: 400, lh: 20 },
    { name: "Caption", size: 12, weight: 400, lh: 16 },
    { name: "Footnote", size: 11, weight: 400, lh: 14 },
  ];
  return (
    <Section
      id="typography"
      title="Typography"
      description="Söhne sans across all products. Two weights vendored in the library: Buch (400) and Halbfett (600). Sizes follow a calm 7-step scale."
    >
      <Specimen>
        <div className="grid gap-3">
          {samples.map((s) => (
            <div
              key={s.name}
              className="grid grid-cols-[120px_120px_1fr] items-center gap-4 border-b py-3 last:border-0"
              style={{ borderColor: "var(--color-rule)" }}
            >
              <span className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-ink-muted)]">
                {s.name}
              </span>
              <span className="font-mono text-[11px] text-[color:var(--color-ink-muted)]">
                {s.size}/{s.lh} · {s.weight === 600 ? "halbfett" : "buch"}
              </span>
              <span
                style={{
                  fontSize: s.size,
                  fontWeight: s.weight,
                  lineHeight: `${s.lh}px`,
                  color: "var(--color-ink)",
                  fontFamily: '"Söhne", system-ui, sans-serif',
                }}
              >
                The quick brown fox jumps over the lazy dog
              </span>
            </div>
          ))}
        </div>
      </Specimen>
    </Section>
  );
}

/** Map of ramp+step → brand-chip name shown as a label on that chip. */
const BRAND_CHIPS: Partial<Record<RampName, Partial<Record<RampStep, string>>>> = {
  warms: { 200: "Beige", 300: "Overlay", 500: "Tope" },
  navy: { 700: "Navy" },
  orange: { 700: "Sunshine" },
  neutral: { 100: "Drawer", 700: "Grey" },
  red: { 500: "#F53646" },
  green: { 500: "#40C152" },
};

/** Steps that should get a 1px primary-color border to make the surface chip
 * stand out from the page (very light) and the brand chip pop. */
const PRIMARY_OUTLINED_CHIPS: Partial<Record<RampName, Partial<Record<RampStep, true>>>> = {
  warms:   { 100: true },
  neutral: { 100: true },
  navy:    { 700: true },
  orange:  { 700: true },
  green:   { 500: true },
  red:     { 500: true },
};

const RAMP_ORDER: RampName[] = [
  "neutral",
  "warms",
  "navy",
  "orange",
  "orangeProposed",
  "purple",
  "green",
  "red",
  "yellow",
  "cyan",
];

/** Semantic role keys, lifted from Buena's design system + Sphere extensions. */
type SemanticRole =
  | "bg-low"
  | "bg-med"
  | "bg-high"
  | "text-primary"
  | "text-second"
  | "text-third"
  | "border-primary"
  | "border-second"
  | "border-third"
  | "button-bg-primary"
  | "button-bg-second"
  | "button-bg-third"
  | "button-text-primary"
  | "button-text-second"
  | "button-text-third"
  | "button-border-primary"
  | "button-border-second"
  | "button-border-third"
  | "icon"
  | "focus-ring"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "chart-1"
  | "chart-2"
  | "chart-3"
  | "chart-4"
  | "chart-5"
  | "chart-6"
  | "chart-7"
  | "chart-8"
  | "chart-9"
  | "chart-10";

type CornerRole = "corner-xl" | "corner-lg" | "corner-md" | "corner-sm";
const CORNER_ROLES: CornerRole[] = ["corner-xl", "corner-lg", "corner-md", "corner-sm"];
const DEFAULT_CORNERS: Record<CornerRole, number> = {
  "corner-xl": 999,
  "corner-lg": 12,
  "corner-md": 8,
  "corner-sm": 4,
};

/** A primitive reference: either a ramp step, or the literal `transparent`. */
type PrimitiveRef = { ramp: RampName; step: RampStep } | "transparent";

const SEMANTIC_ROLES: SemanticRole[] = [
  "bg-low",
  "bg-med",
  "bg-high",
  "text-primary",
  "text-second",
  "text-third",
  "border-primary",
  "border-second",
  "border-third",
  "button-bg-primary",
  "button-bg-second",
  "button-bg-third",
  "button-text-primary",
  "button-text-second",
  "button-text-third",
  "button-border-primary",
  "button-border-second",
  "button-border-third",
  "icon",
  "focus-ring",
  "success",
  "warning",
  "error",
  "info",
  "chart-1",
  "chart-2",
  "chart-3",
  "chart-4",
  "chart-5",
  "chart-6",
  "chart-7",
  "chart-8",
  "chart-9",
  "chart-10",
];

/** Sphere-light defaults — same role names as Buena, but mapped to light-theme
 * primitives. Doug can swap any of these via the dropdowns below. */
const DEFAULT_SEMANTICS: Record<SemanticRole, PrimitiveRef> = {
  "bg-low":              { ramp: "warms",   step: 100 },
  "bg-med":              { ramp: "warms",   step: 200 },
  "bg-high":             { ramp: "warms",   step: 300 },
  "text-primary":        { ramp: "navy",    step: 700 },
  "text-second":         { ramp: "warms",   step: 700 },
  "text-third":          { ramp: "neutral", step: 500 },
  "border-primary":      { ramp: "warms",   step: 400 },
  "border-second":       { ramp: "warms",   step: 300 },
  "border-third":        { ramp: "neutral", step: 300 },
  "button-bg-primary":   { ramp: "orange",  step: 700 },
  "button-bg-second":    "transparent",
  "button-bg-third":     "transparent",
  "button-text-primary": { ramp: "navy",    step: 700 },
  "button-text-second":  { ramp: "navy",    step: 700 },
  "button-text-third":   { ramp: "navy",    step: 700 },
  "button-border-primary": { ramp: "orange", step: 700 },
  "button-border-second":  { ramp: "navy",   step: 700 },
  "button-border-third":   "transparent",
  "icon":                { ramp: "neutral", step: 700 },
  "focus-ring":          { ramp: "navy",    step: 500 },
  "success":             { ramp: "green",   step: 500 },
  "warning":             { ramp: "orange",  step: 700 },
  "error":               { ramp: "red",     step: 500 },
  "info":                { ramp: "navy",    step: 500 },
  "chart-1":             { ramp: "navy",    step: 700 },
  "chart-2":             { ramp: "orange",  step: 700 },
  "chart-3":             { ramp: "purple",  step: 700 },
  "chart-4":             { ramp: "green",   step: 500 },
  "chart-5":             { ramp: "red",     step: 500 },
  "chart-6":             { ramp: "cyan",    step: 700 },
  "chart-7":             { ramp: "yellow",  step: 600 },
  "chart-8":             { ramp: "navy",    step: 400 },
  "chart-9":             { ramp: "purple",  step: 400 },
  "chart-10":            { ramp: "green",   step: 700 },
};

function resolvePrimitive(ref: PrimitiveRef): string {
  if (ref === "transparent") return "transparent";
  return ramps[ref.ramp][ref.step];
}

function refLabel(ref: PrimitiveRef): string {
  if (ref === "transparent") return "transparent";
  return `${ref.ramp}-${ref.step}`;
}

function SectionColor() {
  const [semantics, setSemantics] = useState<Record<SemanticRole, PrimitiveRef>>(DEFAULT_SEMANTICS);
  const [corners, setCorners] = useState<Record<CornerRole, number>>(DEFAULT_CORNERS);
  const setRole = (role: SemanticRole, ref: PrimitiveRef) =>
    setSemantics((prev) => ({ ...prev, [role]: ref }));
  const setCorner = (role: CornerRole, value: number) =>
    setCorners((prev) => ({ ...prev, [role]: value }));

  return (
    <Section
      id="color"
      title="Color"
      description="Two layers. Primitives (the 100–900 ramps) carry the raw values; semantic tokens map onto them. Brand chips (Beige, Overlay, Tope, Navy, Sunshine, Drawer, Grey) occupy fixed positions inside the ramps; Lavender (#BC75D3) is its own value, between purple.700 and the Figma lavender (#CF87E6)."
    >
      <div className="space-y-12">
        <div>
          <h3 className="mb-3 text-[18px] font-semibold tracking-tight text-[color:var(--color-ink)]">
            Primitives
          </h3>
          <div className="space-y-3">
            {RAMP_ORDER.map((name) => (
              <RampRow
                key={name}
                name={name}
                ramp={ramps[name]}
                labels={BRAND_CHIPS[name]}
                outlined={PRIMARY_OUTLINED_CHIPS[name]}
              />
            ))}
          </div>
        </div>

        <div>
          <h3 className="mb-1 text-[18px] font-semibold tracking-tight text-[color:var(--color-ink)]">
            Semantic Tokens
          </h3>
          <p className="mb-4 max-w-[68ch] text-[13px] text-[color:var(--color-body-muted)]">
            Names lifted from Buena&apos;s system. Each token picks a primitive (ramp + step).
            Change a dropdown to swap which primitive feeds that role — the sample app
            below updates live so we can dial in defaults.
          </p>
          <SemanticTokensEditor semantics={semantics} onChange={setRole} />
          <h4 className="mb-2 mt-6 text-[13px] font-semibold tracking-tight text-[color:var(--color-ink)]">
            Corner radii
          </h4>
          <CornerEditor corners={corners} onChange={setCorner} />
        </div>

        <div>
          <h3 className="mb-1 text-[18px] font-semibold tracking-tight text-[color:var(--color-ink)]">
            Sample app
          </h3>
          <p className="mb-4 max-w-[68ch] text-[13px] text-[color:var(--color-body-muted)]">
            Headline, navigation, cards, borders, button states — all driven by the
            semantic tokens above. Buttons use <code className="font-mono">corner-xl</code>;
            cards use <code className="font-mono">corner-md</code>.
          </p>
          <SampleApp semantics={semantics} corners={corners} />
        </div>
      </div>
    </Section>
  );
}

function RampRow({
  name,
  ramp,
  labels,
  outlined,
}: {
  name: RampName;
  ramp: Ramp;
  labels?: Partial<Record<RampStep, string>>;
  outlined?: Partial<Record<RampStep, true>>;
}) {
  const displayName = name === "orangeProposed" ? "Orange Proposed" : name;
  return (
    <div className="space-y-1.5">
      <span className="block text-[12px] font-medium capitalize tracking-[0.01em] text-[color:var(--color-ink)]">
        {displayName}
      </span>
      <div className="grid grid-cols-9 gap-1">
        {RAMP_STEPS.map((step) => (
          <RampChip
            key={step}
            step={step}
            hex={ramp[step]}
            label={labels?.[step]}
            primaryOutline={outlined?.[step]}
          />
        ))}
      </div>
    </div>
  );
}

function RampChip({
  step,
  hex,
  label,
  primaryOutline,
}: {
  step: RampStep;
  hex: string;
  label?: string;
  primaryOutline?: boolean;
}) {
  const onDark = !isLight(hex);
  const fg = onDark ? "rgb(250,250,250)" : "rgb(10,10,10)";
  const border = primaryOutline
    ? "1px solid var(--color-navy)"
    : hex === "#FFFFFF"
      ? "1px solid color-mix(in srgb, var(--color-ink) 10%, transparent)"
      : "none";
  return (
    <button
      type="button"
      title={`Click to copy ${hex}`}
      onClick={() => copyToClipboard(hex)}
      className="group relative cursor-copy rounded-md text-left transition"
      style={{
        background: hex,
        aspectRatio: "1.6 / 1",
        border,
        outline: "none",
        padding: 0,
        color: fg,
      }}
    >
      <span
        className="absolute bottom-1 left-1.5 font-medium tabular-nums"
        style={{ fontSize: 11, lineHeight: "14px", letterSpacing: "0.2px" }}
      >
        {step}
      </span>
      {label && (
        <span
          className="absolute top-1 right-1.5 rounded-sm px-1 py-px text-[9px] font-semibold uppercase tracking-[0.08em]"
          style={{
            background: onDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.08)",
          }}
        >
          {label}
        </span>
      )}
    </button>
  );
}

function SemanticTokensEditor({
  semantics,
  onChange,
}: {
  semantics: Record<SemanticRole, PrimitiveRef>;
  onChange: (role: SemanticRole, ref: PrimitiveRef) => void;
}) {
  return (
    <div
      className="overflow-hidden rounded-md"
      style={{ border: "1px solid var(--color-rule)" }}
    >
      <div
        className="grid grid-cols-[1.2fr_1.4fr_auto_1fr] gap-x-4 px-4 py-2 text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-ink-muted)]"
        style={{ borderBottom: "1px solid var(--color-rule)" }}
      >
        <div>Token</div>
        <div>Primitive</div>
        <div>Chip</div>
        <div>Hex</div>
      </div>
      {SEMANTIC_ROLES.map((role, i) => {
        const ref = semantics[role];
        const hex = resolvePrimitive(ref);
        return (
          <div
            key={role}
            className="grid grid-cols-[1.2fr_1.4fr_auto_1fr] items-center gap-x-4 px-4 py-2 text-[12.5px]"
            style={{
              borderBottom:
                i === SEMANTIC_ROLES.length - 1 ? undefined : "1px solid var(--color-rule)",
            }}
          >
            <code className="font-mono text-[12px] text-[color:var(--color-ink)]">{role}</code>
            <PrimitivePicker value={ref} onChange={(next) => onChange(role, next)} />
            <span
              className="inline-block h-5 w-12 rounded"
              style={{
                background: hex,
                border: "1px solid color-mix(in srgb, var(--color-ink) 10%, transparent)",
              }}
            />
            <code className="font-mono text-[11.5px] text-[color:var(--color-ink-muted)]">
              {hex === "transparent" ? "—" : hex.toLowerCase()}
            </code>
          </div>
        );
      })}
    </div>
  );
}

function CornerEditor({
  corners,
  onChange,
}: {
  corners: Record<CornerRole, number>;
  onChange: (role: CornerRole, value: number) => void;
}) {
  return (
    <div
      className="overflow-hidden rounded-md"
      style={{ border: "1px solid var(--color-rule)" }}
    >
      <div
        className="grid grid-cols-[1fr_120px_1fr] gap-x-4 px-4 py-2 text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-ink-muted)]"
        style={{ borderBottom: "1px solid var(--color-rule)" }}
      >
        <div>Token</div>
        <div>Pixels</div>
        <div>Preview</div>
      </div>
      {CORNER_ROLES.map((role, i) => {
        const value = corners[role];
        return (
          <div
            key={role}
            className="grid grid-cols-[1fr_120px_1fr] items-center gap-x-4 px-4 py-2 text-[12.5px]"
            style={{
              borderBottom:
                i === CORNER_ROLES.length - 1 ? undefined : "1px solid var(--color-rule)",
            }}
          >
            <code className="font-mono text-[12px] text-[color:var(--color-ink)]">{role}</code>
            <input
              type="number"
              min={0}
              max={999}
              value={value}
              onChange={(e) => onChange(role, Number(e.target.value))}
              className="rounded border px-2 py-1 font-mono text-[11.5px]"
              style={{
                borderColor: "var(--color-rule)",
                background: "var(--color-surface)",
                color: "var(--color-ink)",
                width: 100,
              }}
            />
            <span
              className="inline-block h-6 w-16"
              style={{
                background: "var(--color-navy)",
                borderRadius: `${value}px`,
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

function PrimitivePicker({
  value,
  onChange,
}: {
  value: PrimitiveRef;
  onChange: (next: PrimitiveRef) => void;
}) {
  const stringValue = value === "transparent" ? "transparent" : `${value.ramp}-${value.step}`;
  return (
    <select
      value={stringValue}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "transparent") {
          onChange("transparent");
          return;
        }
        const [ramp, step] = v.split("-");
        onChange({ ramp: ramp as RampName, step: Number(step) as RampStep });
      }}
      className="rounded border px-2 py-1 font-mono text-[11.5px]"
      style={{
        borderColor: "var(--color-rule)",
        background: "var(--color-surface)",
        color: "var(--color-ink)",
        minWidth: 140,
      }}
    >
      <option value="transparent">transparent</option>
      {RAMP_ORDER.map((rampName) => {
        const label = rampName === "orangeProposed" ? "Orange Proposed" : rampName;
        return (
          <optgroup key={rampName} label={label}>
            {RAMP_STEPS.map((step) => (
              <option key={step} value={`${rampName}-${step}`}>
                {label} {step} · {ramps[rampName][step]}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

function SampleApp({
  semantics,
  corners,
}: {
  semantics: Record<SemanticRole, PrimitiveRef>;
  corners: Record<CornerRole, number>;
}) {
  const c = (role: SemanticRole) => resolvePrimitive(semantics[role]);
  const cornerCard = `${corners["corner-md"]}px`;

  const cardBorder = `1px solid ${c("border-second")}`;
  const subtleBorder = `1px solid ${c("border-third")}`;

  return (
    <div
      className="overflow-hidden"
      style={{
        border: `1px solid ${c("border-primary")}`,
        background: c("bg-low"),
        borderRadius: `${corners["corner-lg"]}px`,
      }}
    >
      {/* Nav */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ borderBottom: subtleBorder, background: c("bg-med") }}
      >
        <div className="flex items-center gap-6">
          <span
            className="text-[13px] font-semibold tracking-tight"
            style={{ color: c("text-primary") }}
          >
            Sphere
          </span>
          <nav className="flex items-center gap-5 text-[12.5px]" style={{ color: c("text-second") }}>
            <span style={{ color: c("text-primary"), fontWeight: 500 }}>Documents</span>
            <span>Reviews</span>
            <span>Library</span>
            <span>Settings</span>
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="second" semantics={semantics} corners={corners}>Search</Btn>
          <Btn variant="primary" semantics={semantics} corners={corners}>New document</Btn>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-6" style={{ background: c("bg-low") }}>
        <div className="mb-1 text-[11px] uppercase tracking-[0.16em]" style={{ color: c("text-third") }}>
          Q2 Reporting
        </div>
        <h4
          className="mb-1 text-[24px] font-semibold tracking-tight"
          style={{ color: c("text-primary") }}
        >
          Fossil-Free Index — quarterly factsheet
        </h4>
        <p className="mb-5 max-w-[60ch] text-[13px]" style={{ color: c("text-second") }}>
          The current run captures the screening criteria, holdings rollover, and
          performance figures for the period ending March 31. Three reviewers are
          assigned; sign-off due Friday.
        </p>

        {/* Cards */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { title: "Coverage", value: "92%", note: "of S&P 500" },
            { title: "Excluded", value: "41", note: "fossil-fuel companies" },
            { title: "Tracking error", value: "0.38%", note: "vs. benchmark" },
          ].map((card) => (
            <div
              key={card.title}
              className="p-4"
              style={{ background: c("bg-med"), border: cardBorder, borderRadius: cornerCard }}
            >
              <div
                className="mb-1 text-[11px] uppercase tracking-[0.14em]"
                style={{ color: c("text-third") }}
              >
                {card.title}
              </div>
              <div
                className="text-[22px] font-semibold tracking-tight"
                style={{ color: c("text-primary") }}
              >
                {card.value}
              </div>
              <div className="text-[12px]" style={{ color: c("text-second") }}>
                {card.note}
              </div>
            </div>
          ))}
        </div>

        {/* Line chart */}
        <div
          className="mt-5 p-4"
          style={{ background: c("bg-med"), border: cardBorder, borderRadius: cornerCard }}
        >
          <div className="mb-1 flex items-baseline justify-between">
            <div>
              <div
                className="mb-0.5 text-[11px] uppercase tracking-[0.14em]"
                style={{ color: c("text-third") }}
              >
                12-month performance
              </div>
              <div
                className="text-[14px] font-semibold tracking-tight"
                style={{ color: c("text-primary") }}
              >
                Series comparison
              </div>
            </div>
            <Btn variant="third" semantics={semantics} corners={corners}>Download CSV</Btn>
          </div>
          <LineChart
            colors={CHART_ROLES.map((r) => c(r))}
            gridColor={c("border-third")}
            axisLabelColor={c("text-third")}
          />
          <div className="mt-2 grid grid-cols-5 gap-x-3 gap-y-1.5">
            {CHART_ROLES.map((role, i) => (
              <span
                key={role}
                className="inline-flex items-center gap-1.5 text-[11px]"
                style={{ color: c("text-second") }}
              >
                <span
                  className="inline-block h-2 w-3 rounded-sm"
                  style={{ background: c(role) }}
                />
                Series {i + 1}
              </span>
            ))}
          </div>
        </div>

        {/* Row of statuses */}
        <div
          className="mt-5 flex items-center gap-4 p-3"
          style={{ background: c("bg-high"), border: cardBorder, borderRadius: cornerCard }}
        >
          <StatusPill label="Published" color={c("success")} />
          <StatusPill label="Pending review" color={c("warning")} />
          <StatusPill label="Blocking issue" color={c("error")} />
          <StatusPill label="Info — re-run scheduled" color={c("info")} />
        </div>
      </div>
    </div>
  );
}

const CHART_ROLES: SemanticRole[] = [
  "chart-1", "chart-2", "chart-3", "chart-4", "chart-5",
  "chart-6", "chart-7", "chart-8", "chart-9", "chart-10",
];

function Btn({
  variant,
  semantics,
  corners,
  children,
}: {
  variant: "primary" | "second" | "third";
  semantics: Record<SemanticRole, PrimitiveRef>;
  corners: Record<CornerRole, number>;
  children: React.ReactNode;
}) {
  const c = (role: SemanticRole) => resolvePrimitive(semantics[role]);
  const bg = c(`button-bg-${variant}` as SemanticRole);
  const text = c(`button-text-${variant}` as SemanticRole);
  const border = c(`button-border-${variant}` as SemanticRole);
  return (
    <button
      type="button"
      className="px-3 py-1.5 text-[12px] font-semibold"
      style={{
        background: bg,
        color: text,
        border: `1px solid ${border === "transparent" ? "transparent" : border}`,
        borderRadius: `${corners["corner-xl"]}px`,
      }}
    >
      {children}
    </button>
  );
}

/** 10 deterministic random-walk series for the sample app's line chart. */
function generateChartSeries(): number[][] {
  const series: number[][] = [];
  const POINTS = 24;
  for (let s = 0; s < 10; s++) {
    const data: number[] = [];
    let val = 40 + s * 4;
    for (let i = 0; i < POINTS; i++) {
      val += Math.sin(s * 1.3 + i * 0.45) * 5 + Math.cos(s * 0.7 + i * 0.9) * 3.5;
      val = Math.max(8, Math.min(96, val));
      data.push(val);
    }
    series.push(data);
  }
  return series;
}

const CHART_SERIES = generateChartSeries();

function LineChart({
  colors: lineColors,
  gridColor,
  axisLabelColor,
}: {
  colors: string[];
  gridColor: string;
  axisLabelColor: string;
}) {
  const W = 800;
  const H = 220;
  const pad = { l: 32, r: 8, t: 8, b: 22 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const points = CHART_SERIES[0].length;
  const xStep = innerW / (points - 1);
  const yFor = (v: number) => pad.t + innerH * (1 - v / 100);
  const months = ["Apr", "Jun", "Aug", "Oct", "Dec", "Feb"];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: 220, display: "block" }}
    >
      {/* Y grid */}
      {[0, 25, 50, 75, 100].map((v) => (
        <g key={v}>
          <line
            x1={pad.l}
            x2={W - pad.r}
            y1={yFor(v)}
            y2={yFor(v)}
            stroke={gridColor}
            strokeWidth={0.8}
            opacity={0.45}
          />
          <text
            x={pad.l - 6}
            y={yFor(v) + 3}
            fontSize={9}
            textAnchor="end"
            fill={axisLabelColor}
          >
            {v}
          </text>
        </g>
      ))}
      {/* X labels */}
      {months.map((m, i) => {
        const x = pad.l + (innerW * i) / (months.length - 1);
        return (
          <text key={m} x={x} y={H - 6} fontSize={9} textAnchor="middle" fill={axisLabelColor}>
            {m}
          </text>
        );
      })}
      {/* Series */}
      {CHART_SERIES.map((data, s) => (
        <polyline
          key={s}
          fill="none"
          stroke={lineColors[s]}
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          points={data.map((v, i) => `${pad.l + i * xStep},${yFor(v)}`).join(" ")}
        />
      ))}
    </svg>
  );
}

function StatusPill({ label, color }: { label: string; color: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px]">
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

/** Luma-based light/dark test for picking chip text contrast. */
function isLight(hex: string): boolean {
  const m = hex.replace("#", "");
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 170;
}

function copyToClipboard(text: string) {
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    void navigator.clipboard.writeText(text);
  }
}

function SectionMotion() {
  return (
    <Section
      id="motion"
      title="Motion"
      description="Two easing curves and three durations. Drawer slides use cubic ease-in-out; in-place pushes (e.g., attachment swap) use cubic ease-out."
    >
      <Specimen>
        <div className="grid gap-3">
          <Row label="Cubic ease-in-out (drawer)" value={motion.ease.cubicInOut} />
          <Row label="Cubic ease-out (push)" value={motion.ease.cubicOut} />
          <Row label="Fast" value={`${motion.duration.fast}ms`} />
          <Row label="Medium" value={`${motion.duration.medium}ms`} />
          <Row label="Slow" value={`${motion.duration.slow}ms`} />
        </div>
      </Specimen>
    </Section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="grid grid-cols-[1fr_auto] items-center gap-4 border-b py-2.5 last:border-0"
      style={{ borderColor: "var(--color-rule)" }}
    >
      <span className="text-[13px] text-[color:var(--color-ink)]">{label}</span>
      <code className="font-mono text-[11px] text-[color:var(--color-ink-muted)]">{value}</code>
    </div>
  );
}

function SectionLogos() {
  return (
    <Section
      id="logos"
      title="Logos"
      description="Sphere is the brand. The Sphere wordmark ships as two PNG variants — light (for cream/light surfaces) and dark (for navy/dark surfaces). Always render the PNG as-is; never re-draw or recolor."
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Specimen label="Sphere — light variant (for light surfaces)">
          <div className="flex items-center justify-center py-4">
            <img src="/sphere-on-light.png" alt="Sphere" className="h-10 w-auto" />
          </div>
        </Specimen>
        <Specimen label="Sphere — dark variant (for navy / dark surfaces)">
          <div
            className="flex items-center justify-center rounded-md py-6"
            style={{ background: "var(--color-navy)" }}
          >
            <img src="/sphere-on-dark.png" alt="Sphere" className="h-10 w-auto" />
          </div>
        </Specimen>
      </div>
    </Section>
  );
}

function SectionColorPicker() {
  const [value, setValue] = useState<CardColor | null>("navy");
  return (
    <Section
      id="color-picker"
      title="Color Picker"
      description="Brand swatches used to tint Kanban cards in DocuSphere. The fifth circle is a no-color cream that maps to null."
    >
      <Specimen>
        <div className="flex flex-col gap-4">
          <ColorPicker value={value} onChange={setValue} />
          <p className="text-[11.5px] text-[color:var(--color-ink-muted)]">
            Selected: <code className="font-mono">{String(value)}</code>
            {value && (
              <>
                {"  "}·{"  "}
                <span style={{ color: CARD_COLOR_VAR[value] }}>
                  {CARD_COLOR_LABELS[value]} ({colors[value as keyof typeof colors] ?? "—"})
                </span>
              </>
            )}
          </p>
          <p className="text-[11.5px] text-[color:var(--color-ink-muted)]">
            Available: {CARD_COLORS.map((c) => CARD_COLOR_LABELS[c]).join(" · ")}, None.
          </p>
        </div>
      </Specimen>
    </Section>
  );
}

// ─── Spacing ────────────────────────────────────────────────────────────────

const SPACING_SCALE: Array<{ name: string; px: number }> = [
  { name: "xs", px: 4 },
  { name: "sm", px: 8 },
  { name: "md", px: 12 },
  { name: "lg", px: 16 },
  { name: "xl", px: 24 },
  { name: "2xl", px: 32 },
  { name: "3xl", px: 48 },
  { name: "4xl", px: 64 },
];

function SectionSpacing() {
  return (
    <Section
      id="spacing"
      title="Spacing"
      description="8pt grid with a 4pt half step. The square at right is rendered at the actual pixel size."
    >
      <Specimen>
        <div className="space-y-2">
          {SPACING_SCALE.map((s) => (
            <div
              key={s.name}
              className="grid grid-cols-[60px_60px_1fr_auto] items-center gap-4 border-b py-2 last:border-0"
              style={{ borderColor: "var(--color-rule)" }}
            >
              <span className="font-mono text-[11.5px] text-[color:var(--color-ink-muted)]">
                {s.name}
              </span>
              <span className="font-mono text-[11.5px] text-[color:var(--color-ink)]">
                {s.px}px
              </span>
              <span className="text-[12px] text-[color:var(--color-body)]">
                {s.px} × {s.px}
              </span>
              <div style={{ width: s.px, height: s.px, background: "var(--color-navy)" }} />
            </div>
          ))}
        </div>
      </Specimen>
    </Section>
  );
}

// ─── Corners ────────────────────────────────────────────────────────────────

const CORNER_SCALE: Array<{ name: string; px: number | "full" }> = [
  { name: "none", px: 0 },
  { name: "sm", px: 4 },
  { name: "md", px: 8 },
  { name: "lg", px: 12 },
  { name: "xl", px: 999 },
];

function SectionCorners() {
  return (
    <Section
      id="corners"
      title="Corners"
      description="Each card uses its own radius. What you see is what you get. The Sphere defaults: buttons use xl (pill), cards use md, the app shell uses lg."
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {CORNER_SCALE.map((c) => (
          <div
            key={c.name}
            className="flex flex-col items-center justify-center p-5"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-rule)",
              borderRadius: c.px === "full" ? 999 : (c.px as number),
            }}
          >
            <span className="font-mono text-[12px] text-[color:var(--color-ink)]">
              corner-{c.name}
            </span>
            <span className="font-mono text-[10.5px] text-[color:var(--color-ink-muted)]">
              {c.px === 999 ? "999px (pill)" : `${c.px}px`}
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ─── Buttons ────────────────────────────────────────────────────────────────

type BtnVariant = "primary" | "secondary" | "ghost" | "destructive" | "destructive-secondary";
type BtnSize = "sm" | "md" | "lg";

const BTN_SIZE: Record<BtnSize, { padX: number; padY: number; font: number }> = {
  sm: { padX: 10, padY: 4,  font: 12 },
  md: { padX: 14, padY: 7,  font: 13 },
  lg: { padX: 18, padY: 10, font: 15 },
};

function DSButton({
  variant = "primary",
  size = "md",
  pill = true,
  children,
  disabled,
}: {
  variant?: BtnVariant;
  size?: BtnSize;
  pill?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  const { padX, padY, font } = BTN_SIZE[size];
  const styles: React.CSSProperties = (() => {
    switch (variant) {
      case "primary":
        return {
          background: "var(--color-sunshine)",
          color: "var(--color-navy)",
          border: "1px solid var(--color-sunshine)",
        };
      case "secondary":
        return {
          background: "transparent",
          color: "var(--color-navy)",
          border: "1px solid var(--color-navy)",
        };
      case "ghost":
        return {
          background: "transparent",
          color: "var(--color-navy)",
          border: "1px solid transparent",
        };
      case "destructive":
        return {
          background: "var(--color-red-500)",
          color: "white",
          border: "1px solid var(--color-red-500)",
        };
      case "destructive-secondary":
        return {
          background: "transparent",
          color: "var(--color-red-600)",
          border: "1px solid var(--color-red-500)",
        };
    }
  })();
  return (
    <button
      type="button"
      disabled={disabled}
      className="font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        ...styles,
        padding: `${padY}px ${padX}px`,
        fontSize: font,
        lineHeight: 1.1,
        borderRadius: pill ? 999 : 8,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

function SectionButtons() {
  return (
    <Section
      id="buttons"
      title="Buttons"
      description="Five variants × three sizes. Primary uses Sunshine on Navy ink; secondary is outlined Navy; ghost is text-only; destructive uses Red."
    >
      <div className="space-y-4">
        {(["primary", "secondary", "ghost", "destructive", "destructive-secondary"] as BtnVariant[]).map((variant) => (
          <Specimen key={variant} label={variant.replace("-", " · ")}>
            <div className="flex flex-wrap items-center gap-3">
              {(["sm", "md", "lg"] as BtnSize[]).map((size) => (
                <DSButton key={size} variant={variant} size={size}>
                  {size.toUpperCase()} button
                </DSButton>
              ))}
              <DSButton variant={variant} size="md" disabled>
                Disabled
              </DSButton>
              <DSButton variant={variant} size="md" pill={false}>
                Rect 8px
              </DSButton>
            </div>
          </Specimen>
        ))}
      </div>
    </Section>
  );
}

// ─── Pills & Toggles ────────────────────────────────────────────────────────

function HeaderPill({
  options,
  size = "md",
}: {
  options: string[];
  size?: "sm" | "md" | "lg";
}) {
  const [active, setActive] = useState(options[0]);
  const padX = size === "sm" ? 10 : size === "md" ? 14 : 18;
  const padY = size === "sm" ? 4 : size === "md" ? 6 : 8;
  const font = size === "sm" ? 12 : size === "md" ? 13 : 14;
  return (
    <div
      className="inline-flex items-center"
      style={{
        background: "var(--color-warms-200)",
        border: "1px solid var(--color-rule)",
        borderRadius: 999,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((opt) => {
        const isActive = opt === active;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => setActive(opt)}
            className="font-medium transition"
            style={{
              padding: `${padY}px ${padX}px`,
              fontSize: font,
              borderRadius: 999,
              background: isActive ? "var(--color-surface)" : "transparent",
              color: isActive ? "var(--color-navy)" : "var(--color-warms-700)",
              border: isActive ? "1px solid var(--color-navy)" : "1px solid transparent",
              cursor: "pointer",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function SegmentedControl({ options }: { options: string[] }) {
  const [active, setActive] = useState(options[0]);
  return (
    <div
      className="inline-flex items-center"
      style={{
        background: "var(--color-neutral-200)",
        borderRadius: 8,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((opt) => {
        const isActive = opt === active;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => setActive(opt)}
            className="font-medium transition"
            style={{
              padding: "6px 14px",
              fontSize: 12.5,
              borderRadius: 6,
              background: isActive ? "var(--color-surface)" : "transparent",
              color: isActive ? "var(--color-navy)" : "var(--color-warms-700)",
              boxShadow: isActive
                ? "0 1px 2px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)"
                : undefined,
              cursor: "pointer",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function SectionPills() {
  return (
    <Section
      id="pills"
      title="Pills & Toggles"
      description="Compact binary or n-way affordances. All interactive."
    >
      <div className="space-y-4">
        <Specimen label="Header Pills · three sizes">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold tracking-[0.12em] text-[color:var(--color-ink-muted)]">LG</span>
              <HeaderPill options={["Team", "Details", "Activity"]} size="lg" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold tracking-[0.12em] text-[color:var(--color-ink-muted)]">MD</span>
              <HeaderPill options={["Team", "Details", "Activity"]} size="md" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold tracking-[0.12em] text-[color:var(--color-ink-muted)]">SM</span>
              <HeaderPill options={["Team", "Details", "Activity"]} size="sm" />
            </div>
          </div>
        </Specimen>
        <Specimen label="Segmented Controls">
          <div className="flex flex-wrap items-center gap-4">
            <SegmentedControl options={["Day", "Week", "Month"]} />
            <SegmentedControl options={["List", "Board", "Calendar"]} />
            <SegmentedControl options={["All", "Active", "Archived"]} />
          </div>
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Inputs ─────────────────────────────────────────────────────────────────

type InputState = "default" | "focus" | "error" | "warning" | "disabled";

function FieldShell({
  label,
  state,
  children,
  message,
}: {
  label: string;
  state?: InputState;
  children: React.ReactNode;
  message?: string;
}) {
  const color =
    state === "error"
      ? "var(--color-red-600)"
      : state === "warning"
        ? "var(--color-orange-700)"
        : "var(--color-ink-muted)";
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-[0.12em]" style={{ color }}>
        {label}
      </span>
      {children}
      {message && (
        <span className="mt-1 block text-[11px]" style={{ color }}>
          {message}
        </span>
      )}
    </label>
  );
}

function fieldStyle(state: InputState): React.CSSProperties {
  const borderByState: Record<InputState, string> = {
    default:  "var(--color-rule)",
    focus:    "var(--color-navy)",
    error:    "var(--color-red-500)",
    warning:  "var(--color-orange-700)",
    disabled: "var(--color-rule)",
  };
  return {
    width: "100%",
    padding: "8px 12px",
    fontSize: 13,
    background: state === "disabled" ? "var(--color-warms-200)" : "var(--color-surface)",
    color: state === "disabled" ? "var(--color-ink-muted)" : "var(--color-ink)",
    border: `1px solid ${borderByState[state]}`,
    borderRadius: 8,
    outline: "none",
  };
}

function DSInput({
  type = "text",
  state = "default",
  placeholder,
  defaultValue,
}: {
  type?: string;
  state?: InputState;
  placeholder?: string;
  defaultValue?: string;
}) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      defaultValue={defaultValue}
      disabled={state === "disabled"}
      autoFocus={state === "focus"}
      style={fieldStyle(state)}
    />
  );
}

function SectionInputs() {
  return (
    <Section
      id="inputs"
      title="Inputs"
      description="Editable text controls. Hover, focus, type to feel each state."
    >
      <div className="space-y-4">
        <Specimen label="Text Inputs">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <FieldShell label="Default" state="default"><DSInput placeholder="Jane Doe" /></FieldShell>
            <FieldShell label="Focused" state="default"><DSInput defaultValue="Jane Doe" state="focus" /></FieldShell>
            <FieldShell label="Error" state="error" message="Required field"><DSInput defaultValue="" state="error" /></FieldShell>
            <FieldShell label="Warning" state="warning" message="Looks unusual, confirm"><DSInput defaultValue="J@ne D0e" state="warning" /></FieldShell>
            <FieldShell label="Disabled" state="disabled"><DSInput defaultValue="Read-only" state="disabled" /></FieldShell>
          </div>
        </Specimen>
        <Specimen label="Passwords">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <FieldShell label="Default"><DSInput type="password" defaultValue="secret123" /></FieldShell>
            <FieldShell label="Error" state="error" message="Password is too short"><DSInput type="password" defaultValue="abc" state="error" /></FieldShell>
            <FieldShell label="Warning" state="warning" message="Avoid reusing recent passwords"><DSInput type="password" defaultValue="oldpassword" state="warning" /></FieldShell>
          </div>
        </Specimen>
        <Specimen label="Search">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <FieldShell label="Default"><DSInput type="search" placeholder="Search documents…" /></FieldShell>
            <FieldShell label="With value"><DSInput type="search" defaultValue="quarterly factsheet" /></FieldShell>
          </div>
        </Specimen>
        <Specimen label="Textareas">
          <FieldShell label="Default">
            <textarea
              defaultValue="The current run captures the screening criteria and performance figures."
              rows={4}
              style={{ ...fieldStyle("default"), resize: "vertical" as const }}
            />
          </FieldShell>
        </Specimen>
        <Specimen label="Selects">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <FieldShell label="Default">
              <select style={fieldStyle("default")}>
                <option>Fossil-Free Index</option>
                <option>Climate Fund</option>
                <option>S&amp;P 500 Index</option>
              </select>
            </FieldShell>
            <FieldShell label="Disabled" state="disabled">
              <select disabled style={fieldStyle("disabled")}>
                <option>Climate Fund</option>
              </select>
            </FieldShell>
          </div>
        </Specimen>
        <Specimen label="Numbers / Times">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <FieldShell label="Number"><DSInput type="number" defaultValue="92" /></FieldShell>
            <FieldShell label="Time"><DSInput type="time" defaultValue="09:30" /></FieldShell>
            <FieldShell label="Date"><DSInput type="date" defaultValue="2026-05-12" /></FieldShell>
          </div>
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Form Controls ──────────────────────────────────────────────────────────

function DSCheckbox({
  label,
  defaultChecked,
  flavor = "expressive",
}: {
  label: string;
  defaultChecked?: boolean;
  flavor?: "expressive" | "neutral";
}) {
  const [checked, setChecked] = useState(!!defaultChecked);
  const fillColor = flavor === "expressive" ? "var(--color-sunshine)" : "var(--color-navy)";
  return (
    <label className="flex cursor-pointer items-center gap-2 text-[13px]">
      <span
        className="flex items-center justify-center transition"
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          background: checked ? fillColor : "var(--color-surface)",
          border: `1px solid ${checked ? fillColor : "var(--color-rule-strong)"}`,
        }}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5L4 7L8 3" stroke="var(--color-navy)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => setChecked(e.target.checked)}
        className="sr-only"
      />
      <span style={{ color: "var(--color-ink)" }}>{label}</span>
    </label>
  );
}

function DSRadio({
  label,
  name,
  defaultChecked,
  flavor = "expressive",
}: {
  label: string;
  name: string;
  defaultChecked?: boolean;
  flavor?: "expressive" | "neutral";
}) {
  const fillColor = flavor === "expressive" ? "var(--color-sunshine)" : "var(--color-navy)";
  const [checked, setChecked] = useState(!!defaultChecked);
  return (
    <label className="flex cursor-pointer items-center gap-2 text-[13px]">
      <span
        className="flex items-center justify-center"
        style={{
          width: 16,
          height: 16,
          borderRadius: 999,
          background: "var(--color-surface)",
          border: `1px solid ${checked ? fillColor : "var(--color-rule-strong)"}`,
        }}
      >
        {checked && (
          <span style={{ width: 8, height: 8, borderRadius: 999, background: fillColor }} />
        )}
      </span>
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={() => setChecked(true)}
        className="sr-only"
      />
      <span style={{ color: "var(--color-ink)" }}>{label}</span>
    </label>
  );
}

function DSSwitch({
  label,
  defaultOn,
  flavor = "expressive",
}: {
  label: string;
  defaultOn?: boolean;
  flavor?: "expressive" | "neutral";
}) {
  const [on, setOn] = useState(!!defaultOn);
  const onColor = flavor === "expressive" ? "var(--color-sunshine)" : "var(--color-navy)";
  return (
    <label className="flex cursor-pointer items-center gap-2 text-[13px]">
      <span
        onClick={(e) => {
          e.preventDefault();
          setOn(!on);
        }}
        style={{
          position: "relative",
          width: 32,
          height: 18,
          background: on ? onColor : "var(--color-neutral-300)",
          borderRadius: 999,
          transition: "background 150ms",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: on ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: 999,
            background: "white",
            transition: "left 150ms",
            boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
          }}
        />
      </span>
      <span style={{ color: "var(--color-ink)" }}>{label}</span>
    </label>
  );
}

function SectionFormControls() {
  return (
    <Section
      id="form-controls"
      title="Form Controls"
      description="Boolean and multi-choice primitives. Two flavors of each: expressive (Sunshine fill) and neutral (Navy fill)."
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Specimen label="Checkboxes · expressive">
          <div className="flex flex-col gap-2.5">
            <DSCheckbox label="Option one" defaultChecked />
            <DSCheckbox label="Option two" />
            <DSCheckbox label="Option three" defaultChecked />
          </div>
        </Specimen>
        <Specimen label="Checkboxes · neutral">
          <div className="flex flex-col gap-2.5">
            <DSCheckbox label="Option one" defaultChecked flavor="neutral" />
            <DSCheckbox label="Option two" flavor="neutral" />
            <DSCheckbox label="Option three" defaultChecked flavor="neutral" />
          </div>
        </Specimen>
        <Specimen label="Radios · expressive">
          <div className="flex flex-col gap-2.5">
            <DSRadio label="Option one" name="ex-radio" defaultChecked />
            <DSRadio label="Option two" name="ex-radio" />
            <DSRadio label="Option three" name="ex-radio" />
          </div>
        </Specimen>
        <Specimen label="Radios · neutral">
          <div className="flex flex-col gap-2.5">
            <DSRadio label="Option one" name="ne-radio" defaultChecked flavor="neutral" />
            <DSRadio label="Option two" name="ne-radio" flavor="neutral" />
            <DSRadio label="Option three" name="ne-radio" flavor="neutral" />
          </div>
        </Specimen>
        <Specimen label="Switches · expressive">
          <div className="flex flex-col gap-2.5">
            <DSSwitch label="Notifications" defaultOn />
            <DSSwitch label="Auto-publish" />
            <DSSwitch label="Email digests" defaultOn />
          </div>
        </Specimen>
        <Specimen label="Switches · neutral">
          <div className="flex flex-col gap-2.5">
            <DSSwitch label="Notifications" defaultOn flavor="neutral" />
            <DSSwitch label="Auto-publish" flavor="neutral" />
            <DSSwitch label="Email digests" defaultOn flavor="neutral" />
          </div>
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Chips & Tags ───────────────────────────────────────────────────────────

type ChipState = "neutral" | "include" | "exclude";

const CHIP_COLORS: Array<{ name: string; ramp: RampName }> = [
  { name: "Green",  ramp: "green" },
  { name: "Yellow", ramp: "yellow" },
  { name: "Orange", ramp: "orange" },
  { name: "Red",    ramp: "red" },
  { name: "Navy",   ramp: "navy" },
  { name: "Purple", ramp: "purple" },
  { name: "Cyan",   ramp: "cyan" },
];

function PrimaryChip({ name, ramp }: { name: string; ramp: RampName }) {
  const [state, setState] = useState<ChipState>("neutral");
  const r = ramps[ramp];
  const advance = () =>
    setState((s) => (s === "neutral" ? "include" : s === "include" ? "exclude" : "neutral"));
  const styles: Record<ChipState, React.CSSProperties> = {
    neutral: { background: "var(--color-warms-200)", color: "var(--color-warms-800)", border: `1px solid ${r[400]}` },
    include: { background: r[100], color: r[800], border: `1px solid ${r[500]}` },
    exclude: { background: "var(--color-neutral-200)", color: "var(--color-neutral-700)", border: `1px solid var(--color-neutral-400)`, textDecoration: "line-through" },
  };
  return (
    <button
      type="button"
      onClick={advance}
      className="text-[12px] font-medium transition"
      style={{ padding: "4px 10px", borderRadius: 999, cursor: "pointer", ...styles[state] }}
    >
      {name}
    </button>
  );
}

function SecondaryChip({ name, ramp }: { name: string; ramp: RampName }) {
  const r = ramps[ramp];
  return (
    <span
      className="text-[12px] font-medium"
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: "transparent",
        color: r[700],
        border: `1px solid ${r[500]}`,
      }}
    >
      {name}
    </span>
  );
}

function SortChip({ name, dir }: { name: string; dir: "asc" | "desc" }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[12px] font-medium"
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: "var(--color-warms-200)",
        color: "var(--color-navy)",
        border: "1px solid var(--color-warms-400)",
      }}
    >
      {name}
      <span style={{ fontSize: 10 }}>{dir === "asc" ? "↑" : "↓"}</span>
    </span>
  );
}

function StatusBadge({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px]">
      <span style={{ width: 6, height: 6, background: color, borderRadius: 999, display: "inline-block" }} />
      <span style={{ color: "var(--color-ink)" }}>{label}</span>
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          padding: "1px 6px",
          borderRadius: 999,
          background: "var(--color-warms-300)",
          color: "var(--color-warms-800)",
          fontSize: 11,
        }}
      >
        {count}
      </span>
    </span>
  );
}

function SectionChips() {
  return (
    <Section
      id="chips"
      title="Chips & Tags"
      description="Pill-shaped tags. Filters, labels, and status badges."
    >
      <div className="space-y-4">
        <Specimen label="Primary Key Chips · three-state cycle">
          <p className="mb-3 text-[12px] text-[color:var(--color-body-muted)]">
            Click any chip: neutral → include → exclude → neutral.
          </p>
          <div className="flex flex-wrap gap-2">
            {CHIP_COLORS.map((c) => (
              <PrimaryChip key={c.name} name={c.name} ramp={c.ramp} />
            ))}
          </div>
        </Specimen>
        <Specimen label="Secondary Key Chips · outlined">
          <div className="flex flex-wrap gap-2">
            {CHIP_COLORS.map((c) => (
              <SecondaryChip key={c.name} name={c.name} ramp={c.ramp} />
            ))}
          </div>
        </Specimen>
        <Specimen label="Sort Chips">
          <div className="flex flex-wrap gap-2">
            <SortChip name="Title" dir="asc" />
            <SortChip name="Created" dir="desc" />
            <SortChip name="Status" dir="asc" />
            <SortChip name="Owner" dir="desc" />
          </div>
        </Specimen>
        <Specimen label="Status & Count Badges">
          <div className="flex flex-wrap items-center gap-4">
            <StatusBadge label="Published" count={12} color="var(--color-green-500)" />
            <StatusBadge label="Pending"   count={4}  color="var(--color-orange-700)" />
            <StatusBadge label="Blocking"  count={1}  color="var(--color-red-500)" />
            <StatusBadge label="Draft"     count={7}  color="var(--color-warms-500)" />
          </div>
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Tabs ───────────────────────────────────────────────────────────────────

function Tabs({ options, size = "md" }: { options: string[]; size?: "sm" | "md" | "lg" }) {
  const [active, setActive] = useState(options[0]);
  const font = size === "sm" ? 12 : size === "md" ? 14 : 16;
  const pad = size === "sm" ? "6px 10px" : size === "md" ? "8px 12px" : "10px 14px";
  return (
    <div className="inline-flex" style={{ borderBottom: "1px solid var(--color-rule)" }}>
      {options.map((opt) => {
        const isActive = opt === active;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => setActive(opt)}
            className="font-semibold transition"
            style={{
              padding: pad,
              fontSize: font,
              color: isActive ? "var(--color-navy)" : "var(--color-warms-700)",
              borderBottom: `2px solid ${isActive ? "var(--color-sunshine)" : "transparent"}`,
              marginBottom: -1,
              cursor: "pointer",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function SectionTabs() {
  return (
    <Section
      id="tabs"
      title="Standard Tabs"
      description="Underline tabs in three sizes. Selected weight is constant; only color changes so siblings don't shift."
    >
      <Specimen label="Three sizes">
        <div className="flex flex-col gap-6">
          <Tabs options={["Saturday", "Sunday", "Monday"]} size="lg" />
          <Tabs options={["Saturday", "Sunday", "Monday"]} size="md" />
          <Tabs options={["Saturday", "Sunday", "Monday"]} size="sm" />
        </div>
      </Specimen>
    </Section>
  );
}

// ─── Cards ──────────────────────────────────────────────────────────────────

function PrimaryCard({ title, body }: { title: string; body: string }) {
  return (
    <div
      className="p-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-rule)",
        borderRadius: 8,
      }}
    >
      <div className="text-[14px] font-semibold" style={{ color: "var(--color-navy)" }}>{title}</div>
      <div className="mt-1 text-[12.5px]" style={{ color: "var(--color-warms-700)" }}>{body}</div>
    </div>
  );
}

function StatCard({ label, value, delta }: { label: string; value: string; delta?: string }) {
  return (
    <div
      className="p-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-rule)",
        borderRadius: 8,
      }}
    >
      <div className="mb-1 text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--color-ink-muted)" }}>
        {label}
      </div>
      <div className="text-[22px] font-semibold" style={{ color: "var(--color-navy)" }}>{value}</div>
      {delta && (
        <div className="mt-0.5 text-[11.5px]" style={{ color: "var(--color-green-700)" }}>{delta}</div>
      )}
    </div>
  );
}

function SparkCard({ label, value, series }: { label: string; value: string; series: number[] }) {
  const w = 220, h = 36;
  const max = Math.max(...series), min = Math.min(...series);
  const pts = series.map((v, i) => {
    const x = (i / (series.length - 1)) * w;
    const y = h - ((v - min) / (max - min || 1)) * h;
    return `${x},${y}`;
  }).join(" ");
  return (
    <div
      className="p-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-rule)",
        borderRadius: 8,
      }}
    >
      <div className="mb-1 text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--color-ink-muted)" }}>
        {label}
      </div>
      <div className="text-[20px] font-semibold" style={{ color: "var(--color-navy)" }}>{value}</div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" className="mt-2 block">
        <polyline points={pts} fill="none" stroke="var(--color-sunshine)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function ButtonCard({ title, body }: { title: string; body: string }) {
  return (
    <button
      type="button"
      className="block w-full text-left transition"
      style={{
        padding: 16,
        background: "var(--color-surface)",
        border: "1px solid var(--color-rule)",
        borderRadius: 8,
        cursor: "pointer",
      }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[14px] font-semibold" style={{ color: "var(--color-navy)" }}>{title}</div>
        <span style={{ color: "var(--color-ink-muted)" }}>→</span>
      </div>
      <div className="mt-1 text-[12.5px]" style={{ color: "var(--color-warms-700)" }}>{body}</div>
    </button>
  );
}

function SectionCards() {
  return (
    <Section
      id="cards"
      title="Cards"
      description="Container shapes for list rows and dashboards. Top-left aligned text, consistent padding (16px), corner-md (8px)."
    >
      <div className="space-y-4">
        <Specimen label="Primary Cards">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <PrimaryCard title="Sample Title" body="Two-line description fits here for context." />
            <PrimaryCard title="Sample Title" body="Two-line description fits here for context." />
            <PrimaryCard title="Sample Title" body="Two-line description fits here for context." />
          </div>
        </Specimen>
        <Specimen label="Stats Cards">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <StatCard label="Coverage" value="92%" delta="+1.2% MoM" />
            <StatCard label="Excluded" value="41"  delta="−3 this Q" />
            <StatCard label="Tracking" value="0.38%" />
            <StatCard label="AUM" value="$2.1B" delta="+4.6% YoY" />
          </div>
        </Specimen>
        <Specimen label="Stats Cards With Charts">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <SparkCard label="Revenue" value="$1.4M" series={[32, 41, 38, 49, 55, 62, 58, 70, 78, 74, 82, 88]} />
            <SparkCard label="Active users" value="14.2k" series={[12, 14, 13, 18, 22, 20, 28, 32, 30, 36, 38, 42]} />
            <SparkCard label="Errors" value="0.4%" series={[80, 76, 82, 60, 55, 48, 36, 30, 24, 22, 18, 12]} />
          </div>
        </Specimen>
        <Specimen label="Button Cards">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <ButtonCard title="View documents" body="42 documents across 6 review streams." />
            <ButtonCard title="Quarterly factsheet" body="Q2 2026 — three reviewers assigned." />
          </div>
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Drawers ────────────────────────────────────────────────────────────────

function SectionDrawers() {
  return (
    <Section
      id="drawers"
      title="Drawers"
      description="One drawer shape across the whole app. Bottom-up on mobile (rounded top corners only); right-side on desktop (no rounded corners). 250ms cubic-ease-in-out."
    >
      <Specimen label="Drawer specimens · two positions">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div
            style={{
              border: "1px solid var(--color-rule)",
              borderRadius: 8,
              padding: 12,
              background: "var(--color-warms-200)",
              minHeight: 220,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div className="mb-2 text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-ink-muted)" }}>
              Bottom-up · mobile
            </div>
            <div
              style={{
                position: "absolute",
                left: 12,
                right: 12,
                bottom: 12,
                background: "var(--color-drawer)",
                border: "1px solid var(--color-rule)",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                padding: 12,
              }}
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[13px] font-semibold" style={{ color: "var(--color-navy)" }}>Drawer title</span>
                <span style={{ color: "var(--color-ink-muted)", cursor: "pointer" }}>×</span>
              </div>
              <div className="text-[12px]" style={{ color: "var(--color-warms-700)" }}>
                Bottom-up sheet. Rounded only on the top corners.
              </div>
            </div>
          </div>
          <div
            style={{
              border: "1px solid var(--color-rule)",
              borderRadius: 8,
              padding: 12,
              background: "var(--color-warms-200)",
              minHeight: 220,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div className="mb-2 text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-ink-muted)" }}>
              Right-side · desktop
            </div>
            <div
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                bottom: 12,
                width: "60%",
                background: "var(--color-drawer)",
                border: "1px solid var(--color-rule)",
                padding: 12,
              }}
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[13px] font-semibold" style={{ color: "var(--color-navy)" }}>Drawer title</span>
                <span style={{ color: "var(--color-ink-muted)", cursor: "pointer" }}>×</span>
              </div>
              <div className="text-[12px]" style={{ color: "var(--color-warms-700)" }}>
                Right-side drawer — no rounded corners, full height.
              </div>
            </div>
          </div>
        </div>
      </Specimen>
      <div className="mt-4" />
      <Specimen label="Sample Detail · group A">
        <div
          style={{
            background: "var(--color-drawer)",
            border: "1px solid var(--color-rule)",
            padding: 16,
            borderRadius: 8,
          }}
        >
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-[14px] font-semibold" style={{ color: "var(--color-navy)" }}>Fossil-Free Index</div>
              <div className="text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-ink-muted)" }}>Group A · 24 holdings</div>
            </div>
            <DSButton variant="secondary" size="sm">Close</DSButton>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Coverage" value="92%" />
            <StatCard label="Excluded" value="41" />
            <StatCard label="Tracking" value="0.38%" />
          </div>
        </div>
      </Specimen>
    </Section>
  );
}

// ─── Tables ─────────────────────────────────────────────────────────────────

const TABLE_ROWS = [
  { name: "Quarterly Factsheet · Q1 2026", owner: "Alex Chen",  status: "Published", updated: "May 8" },
  { name: "Coverage Report · April",        owner: "Sam Patel",  status: "Pending",   updated: "May 6" },
  { name: "Holdings Snapshot",               owner: "Jordan Lee", status: "Draft",     updated: "May 3" },
  { name: "Exclusions List · 2026",          owner: "Alex Chen",  status: "Published", updated: "Apr 28" },
  { name: "Risk Memo",                       owner: "Sam Patel",  status: "Blocking",  updated: "Apr 22" },
];

function statusColorRamp(status: string): string {
  return status === "Published" ? "var(--color-green-500)"
    : status === "Pending" ? "var(--color-orange-700)"
    : status === "Blocking" ? "var(--color-red-500)"
    : "var(--color-warms-500)";
}

function SectionTables() {
  return (
    <Section
      id="tables"
      title="Tables"
      description="Strict shape. No outer rounded corners. No left/right border. The line below column labels is 1.5px (Sphere's heaviest stroke). Inner dividers use the lighter rule color."
    >
      <div className="space-y-4">
        <Specimen label="Inline Rows · compact">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Document", "Owner", "Status", "Updated"].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      fontSize: 11,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "var(--color-ink-muted)",
                      padding: "8px 12px",
                      borderBottom: "1.5px solid var(--color-navy)",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TABLE_ROWS.map((r, i) => (
                <tr key={r.name}>
                  <td style={{ padding: "8px 12px", fontSize: 13, color: "var(--color-navy)", borderBottom: i === TABLE_ROWS.length - 1 ? undefined : "1px solid var(--color-rule)" }}>
                    {r.name}
                  </td>
                  <td style={{ padding: "8px 12px", fontSize: 12.5, color: "var(--color-warms-700)", borderBottom: i === TABLE_ROWS.length - 1 ? undefined : "1px solid var(--color-rule)" }}>
                    {r.owner}
                  </td>
                  <td style={{ padding: "8px 12px", fontSize: 12.5, borderBottom: i === TABLE_ROWS.length - 1 ? undefined : "1px solid var(--color-rule)" }}>
                    <span className="inline-flex items-center gap-1.5">
                      <span style={{ width: 6, height: 6, borderRadius: 999, background: statusColorRamp(r.status), display: "inline-block" }} />
                      <span style={{ color: "var(--color-ink)" }}>{r.status}</span>
                    </span>
                  </td>
                  <td style={{ padding: "8px 12px", fontSize: 12.5, color: "var(--color-warms-700)", fontVariantNumeric: "tabular-nums", borderBottom: i === TABLE_ROWS.length - 1 ? undefined : "1px solid var(--color-rule)" }}>
                    {r.updated}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Specimen>
        <Specimen label="Full Width Rows · large">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Document", "Owner", "Status", "Updated"].map((h) => (
                  <th key={h} style={{ textAlign: "left", fontSize: 11.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--color-ink-muted)", padding: "12px 16px", borderBottom: "1.5px solid var(--color-navy)" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TABLE_ROWS.slice(0, 3).map((r, i) => (
                <tr key={r.name}>
                  <td style={{ padding: "16px", fontSize: 14, color: "var(--color-navy)", borderBottom: i === 2 ? undefined : "1px solid var(--color-rule-strong)" }}>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-[12px]" style={{ color: "var(--color-warms-700)" }}>Group A · 24 holdings</div>
                  </td>
                  <td style={{ padding: "16px", fontSize: 13, color: "var(--color-warms-700)", borderBottom: i === 2 ? undefined : "1px solid var(--color-rule-strong)" }}>{r.owner}</td>
                  <td style={{ padding: "16px", fontSize: 13, borderBottom: i === 2 ? undefined : "1px solid var(--color-rule-strong)" }}>
                    <span className="inline-flex items-center gap-1.5">
                      <span style={{ width: 6, height: 6, borderRadius: 999, background: statusColorRamp(r.status), display: "inline-block" }} />
                      <span style={{ color: "var(--color-ink)" }}>{r.status}</span>
                    </span>
                  </td>
                  <td style={{ padding: "16px", fontSize: 13, color: "var(--color-warms-700)", fontVariantNumeric: "tabular-nums", borderBottom: i === 2 ? undefined : "1px solid var(--color-rule-strong)" }}>{r.updated}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Dates ──────────────────────────────────────────────────────────────────

const DATE_TIERS: Array<{ targetLabel: string; output: string; tier: string }> = [
  { targetLabel: "in 14 minutes",   output: "9:30am",                tier: "Tier 1 — time" },
  { targetLabel: "in 3 hours",      output: "12:30pm",               tier: "Tier 1 — time" },
  { targetLabel: "tomorrow",         output: "Thu 8am",               tier: "Tier 2 — weekday + time" },
  { targetLabel: "in 5 days",        output: "Mon May 18",            tier: "Tier 2 — weekday + date" },
  { targetLabel: "this Saturday",    output: "Sat May 16",            tier: "Tier 2 — weekday + date" },
  { targetLabel: "in 2 weeks",       output: "Wed May 27",            tier: "Tier 2 — weekday + date" },
  { targetLabel: "in 3 months",      output: "Aug 12, 2026",          tier: "Tier 3 — full date" },
  { targetLabel: "next year",        output: "May 12, 2027",          tier: "Tier 3 — full date" },
];

function MiniDatePicker({ withYear = false }: { withYear?: boolean }) {
  const days = ["S", "M", "T", "W", "T", "F", "S"];
  const cells = Array.from({ length: 35 }, (_, i) => i + 1 - 4);
  const today = 12;
  const selected = 18;
  return (
    <div
      style={{
        display: "inline-grid",
        gridTemplateColumns: withYear ? "auto 1fr" : "1fr",
        gap: 12,
        background: "var(--color-surface)",
        border: "1px solid var(--color-rule)",
        borderRadius: 8,
        padding: 12,
      }}
    >
      {withYear && (
        <div className="flex flex-col gap-1 text-[11px]" style={{ color: "var(--color-warms-700)", borderRight: "1px solid var(--color-rule)", paddingRight: 10 }}>
          {["2024", "2025", "2026", "2027", "2028"].map((y) => (
            <div
              key={y}
              style={{
                padding: "4px 8px",
                borderRadius: 4,
                background: y === "2026" ? "var(--color-warms-300)" : "transparent",
                color: y === "2026" ? "var(--color-navy)" : "inherit",
                fontWeight: y === "2026" ? 600 : 400,
              }}
            >
              {y}
            </div>
          ))}
        </div>
      )}
      <div>
        <div className="mb-2 flex items-center justify-between text-[12.5px]">
          <span style={{ color: "var(--color-ink-muted)", cursor: "pointer" }}>‹</span>
          <span className="font-semibold" style={{ color: "var(--color-navy)" }}>May 2026</span>
          <span style={{ color: "var(--color-ink-muted)", cursor: "pointer" }}>›</span>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10.5px]" style={{ color: "var(--color-ink-muted)" }}>
          {days.map((d, i) => <div key={i}>{d}</div>)}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-1 text-center text-[12px]" style={{ fontVariantNumeric: "tabular-nums" }}>
          {cells.map((d, i) => {
            const inMonth = d > 0 && d <= 31;
            const isToday = d === today;
            const isSel = d === selected;
            return (
              <div
                key={i}
                style={{
                  padding: 4,
                  borderRadius: 999,
                  color: !inMonth ? "var(--color-warms-400)" : isSel ? "white" : "var(--color-ink)",
                  background: isSel ? "var(--color-navy)" : isToday ? "var(--color-warms-300)" : "transparent",
                  fontWeight: isSel || isToday ? 600 : 400,
                  cursor: inMonth ? "pointer" : "default",
                }}
              >
                {inMonth ? d : ""}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SectionDates() {
  return (
    <Section
      id="dates"
      title="Dates"
      description="A single formatter handles every date in the system. Three tiers depending on how far the target date is from today."
    >
      <div className="space-y-4">
        <Specimen label="Date Logic · today = Tue May 12, 2026">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Target", "Output", "Tier"].map((h) => (
                  <th key={h} style={{ textAlign: "left", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--color-ink-muted)", padding: "8px 12px", borderBottom: "1.5px solid var(--color-navy)" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DATE_TIERS.map((d, i) => (
                <tr key={d.targetLabel}>
                  <td style={{ padding: "8px 12px", fontSize: 13, color: "var(--color-warms-700)", borderBottom: i === DATE_TIERS.length - 1 ? undefined : "1px solid var(--color-rule)" }}>{d.targetLabel}</td>
                  <td style={{ padding: "8px 12px", fontSize: 13, color: "var(--color-navy)", fontWeight: 600, borderBottom: i === DATE_TIERS.length - 1 ? undefined : "1px solid var(--color-rule)" }}>{d.output}</td>
                  <td style={{ padding: "8px 12px", fontSize: 12, color: "var(--color-ink-muted)", borderBottom: i === DATE_TIERS.length - 1 ? undefined : "1px solid var(--color-rule)" }}>{d.tier}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Specimen>
        <Specimen label="Date Picker"><MiniDatePicker /></Specimen>
        <Specimen label="Date Picker · with year column"><MiniDatePicker withYear /></Specimen>
      </div>
    </Section>
  );
}

// ─── Calendar ───────────────────────────────────────────────────────────────

function SectionCalendar() {
  const hours = ["8:00", "9:00", "10:00", "11:00", "12:00", "1:00"];
  const groups = ["Group A", "Group B", "Group C"];
  return (
    <Section
      id="calendar"
      title="Calendar"
      description="Headers and row labels as the app draws them. The Navy 1.5px line below column labels matches the table grammar; half-hour dividers use rule-strong; 15-minute ticks use the lighter rule."
    >
      <Specimen label="Headers & Row Labels">
        <div style={{ border: "1px solid var(--color-rule)", borderRadius: 8, overflow: "hidden", background: "var(--color-surface)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "60px repeat(3, 1fr)", borderBottom: "1.5px solid var(--color-navy)" }}>
            <div />
            {groups.map((g) => (
              <div key={g} style={{ padding: "8px 12px", fontSize: 11.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--color-ink-muted)" }}>
                {g}
              </div>
            ))}
          </div>
          {hours.map((h, i) => (
            <div key={h} style={{ display: "grid", gridTemplateColumns: "60px repeat(3, 1fr)", borderBottom: i === hours.length - 1 ? undefined : "1px solid var(--color-rule-strong)" }}>
              <div style={{ padding: "10px 8px", fontSize: 11, color: "var(--color-ink-muted)", textAlign: "right", borderRight: "1px solid var(--color-rule)", fontVariantNumeric: "tabular-nums" }}>{h}</div>
              {groups.map((g) => (
                <div key={g} style={{ height: 48, borderRight: g !== groups[groups.length - 1] ? "1px solid var(--color-rule)" : "none", position: "relative" }}>
                  <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 0, borderTop: "1px dashed var(--color-rule)" }} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Specimen>
    </Section>
  );
}

// ─── Drag & Sort ────────────────────────────────────────────────────────────

function ResizableChip({ initialW }: { initialW: number }) {
  const [w, setW] = useState(initialW);
  const [selected, setSelected] = useState(false);
  const dragging = useRef(false);
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setW((prev) => Math.max(80, Math.min(360, prev + e.movementX)));
    };
    const onUp = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);
  return (
    <div
      onClick={() => setSelected(!selected)}
      style={{
        position: "relative",
        width: w,
        background: "var(--color-purple-200)",
        color: "var(--color-purple-800)",
        border: `1px solid ${selected ? "var(--color-navy)" : "var(--color-purple-400)"}`,
        borderRadius: 6,
        padding: "8px 12px",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <div className="truncate text-[12.5px] font-medium">Practice · 6th–8th grade strength</div>
      {selected && (
        <div
          onMouseDown={(e) => { e.stopPropagation(); dragging.current = true; }}
          style={{
            position: "absolute",
            top: 0,
            right: -3,
            bottom: 0,
            width: 6,
            cursor: "ew-resize",
            background: "var(--color-navy)",
            borderRadius: 3,
          }}
        />
      )}
    </div>
  );
}

function SortableList() {
  const [items, setItems] = useState(["Coverage Report", "Holdings Snapshot", "Risk Memo", "Exclusions List"]);
  const drag = useRef<number | null>(null);
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => (
        <div
          key={it}
          draggable
          onDragStart={() => (drag.current = i)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            const from = drag.current;
            if (from === null || from === i) return;
            const next = items.slice();
            const [moved] = next.splice(from, 1);
            next.splice(i, 0, moved);
            setItems(next);
            drag.current = null;
          }}
          className="flex items-center gap-2"
          style={{
            padding: "8px 12px",
            background: "var(--color-surface)",
            border: "1px solid var(--color-rule)",
            borderRadius: 6,
            cursor: "grab",
          }}
        >
          <span style={{ color: "var(--color-ink-muted)" }}>⋮⋮</span>
          <span className="text-[13px]" style={{ color: "var(--color-navy)" }}>{it}</span>
        </div>
      ))}
    </div>
  );
}

function SectionDragSort() {
  return (
    <Section
      id="drag-sort"
      title="Drag & Sort"
      description="Affordances that signal grab me. Both examples are interactive."
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Specimen label="Chip Resize">
          <p className="mb-3 text-[12px]" style={{ color: "var(--color-body-muted)" }}>
            Click to select; drag the right-side handle to resize.
          </p>
          <div className="flex flex-col gap-3">
            <ResizableChip initialW={180} />
            <ResizableChip initialW={240} />
          </div>
        </Specimen>
        <Specimen label="Sortable Rows">
          <p className="mb-3 text-[12px]" style={{ color: "var(--color-body-muted)" }}>
            Drag a row by its handle to reorder.
          </p>
          <SortableList />
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Tooltips ───────────────────────────────────────────────────────────────

function HoverTooltip({ label, tip }: { label: string; tip: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: 6,
          border: "1px solid var(--color-rule)",
          color: "var(--color-navy)",
          cursor: "help",
          fontSize: 13,
        }}
      >
        {label}
      </span>
      {open && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "6px 10px",
            background: "var(--color-navy)",
            color: "var(--color-warms-100)",
            fontSize: 11.5,
            borderRadius: 6,
            whiteSpace: "nowrap",
            zIndex: 10,
          }}
        >
          {tip}
        </span>
      )}
    </span>
  );
}

function StaticTooltip({ tip, variant }: { tip: string; variant: "dark" | "light" | "accent" }) {
  const styles: React.CSSProperties = variant === "dark"
    ? { background: "var(--color-navy)", color: "var(--color-warms-100)" }
    : variant === "light"
      ? { background: "var(--color-surface)", color: "var(--color-navy)", border: "1px solid var(--color-rule)" }
      : { background: "var(--color-sunshine)", color: "var(--color-navy)" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "6px 10px",
        borderRadius: 6,
        fontSize: 11.5,
        whiteSpace: "nowrap",
        ...styles,
      }}
    >
      {tip}
    </span>
  );
}

function SectionTooltips() {
  return (
    <Section
      id="tooltips"
      title="Tooltips"
      description="Three styles across two rows. Top row interactive; bottom shows the open state statically for inspection."
    >
      <div className="space-y-4">
        <Specimen label="Hoverable">
          <div className="flex items-center gap-6">
            <HoverTooltip label="?" tip="Quick help" />
            <HoverTooltip label="i" tip="Methodology details" />
            <HoverTooltip label="↑" tip="Sort ascending" />
          </div>
        </Specimen>
        <Specimen label="Static Layouts">
          <div className="flex items-center gap-4">
            <StaticTooltip tip="Dark — default" variant="dark" />
            <StaticTooltip tip="Light — on dark surfaces" variant="light" />
            <StaticTooltip tip="Accent — for callouts" variant="accent" />
          </div>
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Icons ──────────────────────────────────────────────────────────────────

const ICON_PATHS: Record<string, React.ReactNode> = {
  ArrowUp: <path d="M12 5v14m0-14l-6 6m6-6l6 6" />,
  ArrowDown: <path d="M12 5v14m0 0l-6-6m6 6l6-6" />,
  ArrowLeft: <path d="M5 12h14m-14 0l6-6m-6 6l6 6" />,
  ArrowRight: <path d="M5 12h14m0 0l-6-6m6 6l-6 6" />,
  ChevronUp: <path d="M6 15l6-6 6 6" />,
  ChevronDown: <path d="M6 9l6 6 6-6" />,
  ChevronLeft: <path d="M15 6l-6 6 6 6" />,
  ChevronRight: <path d="M9 6l6 6-6 6" />,
  Plus: <path d="M12 5v14m-7-7h14" />,
  Minus: <path d="M5 12h14" />,
  Check: <path d="M5 12l5 5 9-11" />,
  X: <path d="M6 6l12 12m0-12l-12 12" />,
  Search: <><circle cx="11" cy="11" r="6" /><path d="M16 16l4 4" /></>,
  Settings: <><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 0 0-.13-1.34l2.06-1.59-2-3.46-2.4.96a7 7 0 0 0-2.32-1.34l-.36-2.56h-4l-.36 2.56a7 7 0 0 0-2.32 1.34l-2.4-.96-2 3.46 2.06 1.59A7 7 0 0 0 5 12c0 .46.05.91.13 1.34L3.07 14.93l2 3.46 2.4-.96A7 7 0 0 0 9.8 18.77L10.16 21.33h4l.36-2.56a7 7 0 0 0 2.32-1.34l2.4.96 2-3.46-2.06-1.59c.08-.43.13-.88.13-1.34Z" /></>,
  Calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></>,
  Home: <path d="M3 11l9-8 9 8M5 10v10h14V10" />,
  User: <><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6" /></>,
  Bell: <><path d="M6 8a6 6 0 0 1 12 0v5l2 3H4l2-3Z" /><path d="M10 19a2 2 0 0 0 4 0" /></>,
  Mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>,
  Trash: <><path d="M4 7h16" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" /></>,
  Edit: <><path d="M4 20h4l11-11-4-4L4 16Z" /><path d="M14 6l4 4" /></>,
  Download: <><path d="M12 3v14m0 0l-5-5m5 5l5-5" /><path d="M5 21h14" /></>,
  Upload: <><path d="M12 21V7m0 0l-5 5m5-5l5 5" /><path d="M5 3h14" /></>,
};

function Icon({ name }: { name: string }) {
  return (
    <div className="flex flex-col items-center gap-1 p-2.5">
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ color: "var(--color-navy)" }}
      >
        {ICON_PATHS[name]}
      </svg>
      <span className="text-[10px]" style={{ color: "var(--color-ink-muted)" }}>{name}</span>
    </div>
  );
}

function SectionIcons() {
  const groups: Record<string, string[]> = {
    Arrows:   ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "ChevronUp", "ChevronDown", "ChevronLeft", "ChevronRight"],
    Actions:  ["Plus", "Minus", "Check", "X", "Search", "Settings", "Edit", "Trash"],
    Objects:  ["Calendar", "Home", "User", "Bell", "Mail"],
    Transfer: ["Download", "Upload"],
  };
  return (
    <Section
      id="icons"
      title="Icons"
      description="Stroke icons, 22px on a 1.75 stroke. Grouped by intent so the catalogue scans by purpose."
    >
      <div className="space-y-4">
        {Object.entries(groups).map(([group, names]) => (
          <Specimen key={group} label={group}>
            <div className="flex flex-wrap items-start gap-1">
              {names.map((n) => <Icon key={n} name={n} />)}
            </div>
          </Specimen>
        ))}
      </div>
    </Section>
  );
}

// ─── Toasts ─────────────────────────────────────────────────────────────────

function Toast({
  message,
  variant = "success",
}: {
  message: string;
  variant?: "success" | "info" | "warning" | "error";
}) {
  const iconColor: Record<typeof variant, string> = {
    success: "var(--color-navy)",
    info:    "var(--color-cyan-700)",
    warning: "var(--color-orange-700)",
    error:   "var(--color-red-500)",
  } as Record<"success" | "info" | "warning" | "error", string>;
  const icon: Record<typeof variant, React.ReactNode> = {
    success: <path d="M5 12l5 5 9-11" />,
    info:    <><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></>,
    warning: <><path d="M12 3l10 18H2L12 3z" /><path d="M12 10v5M12 18h.01" /></>,
    error:   <><circle cx="12" cy="12" r="9" /><path d="M9 9l6 6M15 9l-6 6" /></>,
  } as Record<"success" | "info" | "warning" | "error", React.ReactNode>;
  return (
    <div
      className="inline-flex items-center"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-rule)",
        borderRadius: 12,
        padding: "12px 16px",
        gap: 12,
        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={iconColor[variant]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {icon[variant]}
      </svg>
      <span style={{ width: 1, height: 16, background: "var(--color-rule)" }} />
      <span className="text-[13.5px] font-medium" style={{ color: "var(--color-navy)" }}>{message}</span>
    </div>
  );
}

function SectionToasts() {
  return (
    <Section
      id="toasts"
      title="Toasts"
      description="Brief confirmations. White card with subtle shadow, leading icon, divider rule, and the message. Four state-mapped variants."
    >
      <Specimen label="Variants">
        <div className="flex flex-wrap items-start gap-4">
          <Toast message="Changes saved" variant="success" />
          <Toast message="Registration successful" variant="success" />
          <Toast message="New version available" variant="info" />
          <Toast message="Storage almost full" variant="warning" />
          <Toast message="Couldn't save changes" variant="error" />
        </div>
      </Specimen>
    </Section>
  );
}

// ─── Avatars ────────────────────────────────────────────────────────────────

const AVATAR_BG_URL = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&auto=format&fit=crop";

function Avatar({
  size = 40,
  src,
  initials = "AC",
  ring = false,
  badge,
}: {
  size?: number;
  src?: string;
  initials?: string;
  ring?: boolean;
  badge?: "online" | "alert" | "edit";
}) {
  return (
    <span style={{ position: "relative", display: "inline-block", width: size, height: size }}>
      <span
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          background: "var(--color-warms-300)",
          color: "var(--color-navy)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: Math.max(10, Math.round(size * 0.35)),
          fontWeight: 600,
          backgroundImage: src ? `url(${src})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          border: ring ? "2px solid var(--color-sunshine)" : "1px solid var(--color-rule)",
          overflow: "hidden",
        }}
      >
        {!src && initials}
      </span>
      {badge === "online" && (
        <span
          style={{
            position: "absolute", right: 0, bottom: 0,
            width: Math.max(8, size * 0.22), height: Math.max(8, size * 0.22),
            background: "var(--color-green-500)",
            border: "2px solid var(--color-surface)",
            borderRadius: 999,
          }}
        />
      )}
      {badge === "alert" && (
        <span
          style={{
            position: "absolute", right: 0, top: 0,
            width: Math.max(8, size * 0.22), height: Math.max(8, size * 0.22),
            background: "var(--color-red-500)",
            border: "2px solid var(--color-surface)",
            borderRadius: 999,
          }}
        />
      )}
      {badge === "edit" && (
        <span
          style={{
            position: "absolute", right: -2, bottom: -2,
            width: Math.max(16, size * 0.35), height: Math.max(16, size * 0.35),
            background: "var(--color-surface)",
            border: "1px solid var(--color-rule)",
            borderRadius: 999,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <svg width={Math.max(8, size * 0.16)} height={Math.max(8, size * 0.16)} viewBox="0 0 24 24" fill="none" stroke="var(--color-navy)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="7" width="18" height="13" rx="2" />
            <path d="M9 7l1.5-3h3L15 7" />
            <circle cx="12" cy="13" r="3" />
          </svg>
        </span>
      )}
    </span>
  );
}

function AvatarStack({ count = 4, size = 32 }: { count?: number; size?: number }) {
  const items = ["AC", "SP", "JL", "MK", "RW", "NB"].slice(0, count);
  return (
    <span className="inline-flex items-center" style={{ marginLeft: 6 }}>
      {items.map((i, idx) => (
        <span key={i} style={{ marginLeft: -8, zIndex: items.length - idx, position: "relative" }}>
          <Avatar size={size} initials={i} />
        </span>
      ))}
      <span
        style={{
          marginLeft: -8,
          width: size, height: size,
          borderRadius: 999,
          background: "var(--color-warms-200)",
          border: "1px solid var(--color-rule)",
          color: "var(--color-warms-700)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: Math.max(10, size * 0.32),
          fontWeight: 600,
        }}
      >
        +3
      </span>
    </span>
  );
}

function SectionAvatars() {
  return (
    <Section
      id="avatars"
      title="Avatars"
      description="Circular identity. Five sizes, with optional status dot or edit overlay. Falls back to initials when no photo is available."
    >
      <div className="space-y-4">
        <Specimen label="Sizes">
          <div className="flex items-end gap-4">
            {[24, 32, 40, 56, 72].map((s) => (
              <div key={s} className="flex flex-col items-center gap-1">
                <Avatar size={s} src={AVATAR_BG_URL} />
                <span className="text-[10.5px] font-mono" style={{ color: "var(--color-ink-muted)" }}>{s}px</span>
              </div>
            ))}
          </div>
        </Specimen>
        <Specimen label="With badge / overlay">
          <div className="flex items-end gap-6">
            <Avatar size={56} src={AVATAR_BG_URL} badge="online" />
            <Avatar size={56} src={AVATAR_BG_URL} badge="alert" />
            <Avatar size={56} src={AVATAR_BG_URL} badge="edit" />
            <Avatar size={56} src={AVATAR_BG_URL} ring />
            <Avatar size={56} initials="JL" />
          </div>
        </Specimen>
        <Specimen label="Stack">
          <AvatarStack count={4} size={36} />
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Navigation ─────────────────────────────────────────────────────────────

const NAV_ITEMS: Array<{ id: string; label: string; icon: React.ReactNode }> = [
  { id: "portfolio", label: "Portfolio", icon: <path d="M4 20V8m6 12V4m6 16v-9m6 9V12" /> },
  { id: "impact",    label: "Impact",    icon: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></> },
  { id: "overview",  label: "Overview",  icon: <path d="M3 11l9-8 9 8M5 10v10h14V10" /> },
  { id: "transfer",  label: "Transfer",  icon: <><path d="M3 8h14m0 0l-4-4m4 4l-4 4" /><path d="M21 16H7m0 0l4 4m-4-4l4-4" /></> },
  { id: "profile",   label: "Profile",   icon: <><circle cx="12" cy="9" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" /></> },
];

function BottomNav({
  selected,
  hideLabels = false,
}: {
  selected?: string;
  hideLabels?: boolean;
}) {
  const [active, setActive] = useState(selected ?? NAV_ITEMS[0].id);
  return (
    <div
      className="inline-flex items-center"
      style={{
        background: "var(--color-warms-100)",
        border: "1px solid var(--color-rule)",
        borderRadius: 999,
        padding: 6,
        gap: 4,
        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
      }}
    >
      {NAV_ITEMS.map((item) => {
        const isActive = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => setActive(item.id)}
            className="flex flex-col items-center justify-center transition"
            style={{
              padding: hideLabels ? 12 : "6px 14px",
              minWidth: hideLabels ? 48 : 70,
              gap: 2,
              background: isActive ? "var(--color-warms-300)" : "transparent",
              color: isActive ? "var(--color-navy)" : "var(--color-warms-700)",
              border: isActive ? "1px solid var(--color-warms-500)" : "1px solid transparent",
              borderRadius: 999,
              cursor: "pointer",
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              {item.icon}
            </svg>
            {!hideLabels && (
              <span className="text-[11px] font-medium">{item.label}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function TopBar() {
  return (
    <div
      className="flex items-center justify-between"
      style={{
        background: "var(--color-warms-100)",
        borderBottom: "1px solid var(--color-rule)",
        padding: "12px 20px",
      }}
    >
      <div className="flex items-center gap-3">
        <img src="/sphere-on-light.png" alt="Sphere" style={{ height: 26, width: "auto" }} />
        <span className="hidden md:inline" style={{ width: 1, height: 22, background: "var(--color-rule)" }} />
        <span
          className="hidden text-[13px] md:inline"
          style={{ color: "var(--color-warms-700)" }}
        >
          Portfolio
        </span>
      </div>
      <div className="flex items-center gap-2">
        <DSInput type="search" placeholder="Search…" />
        <button
          type="button"
          aria-label="Notifications"
          style={{
            width: 36, height: 36,
            borderRadius: 999,
            background: "transparent",
            border: "1px solid var(--color-rule)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
            color: "var(--color-navy)",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 8a6 6 0 0 1 12 0v5l2 3H4l2-3Z" />
            <path d="M10 19a2 2 0 0 0 4 0" />
          </svg>
        </button>
        <Avatar size={36} src={AVATAR_BG_URL} />
      </div>
    </div>
  );
}

function SectionNavigation() {
  return (
    <Section
      id="navigation"
      title="Navigation"
      description="The Atmosphere-style bottom pill is Sphere's primary mobile nav. The top bar pairs the wordmark, context, search, alerts and avatar."
    >
      <div className="space-y-4">
        <Specimen label="Bottom Nav · with labels">
          <div className="flex justify-center py-3"><BottomNav selected="profile" /></div>
        </Specimen>
        <Specimen label="Bottom Nav · selected mid-position">
          <div className="flex justify-center py-3"><BottomNav selected="overview" /></div>
        </Specimen>
        <Specimen label="Bottom Nav · icon-only (compact)">
          <div className="flex justify-center py-3"><BottomNav selected="impact" hideLabels /></div>
        </Specimen>
        <Specimen label="Top Bar">
          <TopBar />
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Charts ─────────────────────────────────────────────────────────────────

function StackedAreaChart({
  series,
  colors: cs,
}: {
  series: number[][];
  colors: string[];
}) {
  const W = 800, H = 260;
  const pad = { l: 40, r: 12, t: 16, b: 24 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const points = series[0].length;
  const xStep = innerW / (points - 1);
  const stacked: number[][] = [];
  for (let i = 0; i < points; i++) {
    let acc = 0;
    const col: number[] = [];
    for (let s = 0; s < series.length; s++) {
      acc += series[s][i];
      col.push(acc);
    }
    stacked.push(col);
  }
  const maxVal = Math.max(...stacked.map((c) => c[c.length - 1]));
  const yFor = (v: number) => pad.t + innerH * (1 - v / maxVal);
  const tooltipIdx = Math.floor(points * 0.4);

  const paths: string[] = [];
  for (let s = 0; s < series.length; s++) {
    const topVals: number[] = stacked.map((c) => c[s]);
    const bottomVals: number[] = s === 0 ? new Array(points).fill(0) : stacked.map((c) => c[s - 1]);
    let d = "";
    topVals.forEach((v, i) => {
      const x = pad.l + i * xStep;
      d += (i === 0 ? "M" : "L") + `${x},${yFor(v)} `;
    });
    for (let i = points - 1; i >= 0; i--) {
      const x = pad.l + i * xStep;
      d += `L${x},${yFor(bottomVals[i])} `;
    }
    d += "Z";
    paths.push(d);
  }

  const months = ["Jan 1", "Feb 1", "Mar 1", "Apr 1", "May 1", "Jun 1", "Jul 1"];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: 260, display: "block" }}>
      {[0, 0.25, 0.5, 0.75, 1].map((p) => {
        const y = pad.t + innerH * (1 - p);
        const v = Math.round((p * maxVal) / 1000) * 1000;
        return (
          <g key={p}>
            <line x1={pad.l} x2={W - pad.r} y1={y} y2={y} stroke="var(--color-rule)" strokeWidth={0.8} opacity={0.5} />
            <text x={pad.l - 6} y={y + 3} fontSize={10} textAnchor="end" fill="var(--color-ink-muted)">${v / 1000}k</text>
          </g>
        );
      })}
      {paths.map((d, i) => (
        <path key={i} d={d} fill={cs[i]} opacity={0.85} />
      ))}
      {/* Tooltip line */}
      <line
        x1={pad.l + tooltipIdx * xStep}
        x2={pad.l + tooltipIdx * xStep}
        y1={pad.t}
        y2={pad.t + innerH}
        stroke="var(--color-navy)"
        strokeWidth={1}
        opacity={0.4}
      />
      {series.map((_, s) => (
        <circle
          key={s}
          cx={pad.l + tooltipIdx * xStep}
          cy={yFor(stacked[tooltipIdx][s])}
          r="3.5"
          fill="var(--color-surface)"
          stroke={cs[s]}
          strokeWidth={2}
        />
      ))}
      {/* X labels */}
      {months.map((m, i) => {
        const x = pad.l + (innerW * i) / (months.length - 1);
        return <text key={m} x={x} y={H - 6} fontSize={10} textAnchor="middle" fill="var(--color-ink-muted)">{m}</text>;
      })}
    </svg>
  );
}

function BarList({ items, accent }: { items: Array<{ label: string; value: string; magnitude: number }>; accent: string }) {
  const max = Math.max(...items.map((i) => i.magnitude));
  return (
    <div className="space-y-2.5">
      {items.map((it) => {
        const pct = (it.magnitude / max) * 100;
        return (
          <div key={it.label}>
            <div className="mb-1 flex items-center justify-between text-[12.5px]">
              <span style={{ color: "var(--color-navy)" }}>{it.label}</span>
              <span style={{ color: "var(--color-warms-700)", fontVariantNumeric: "tabular-nums" }}>{it.value}</span>
            </div>
            <div
              style={{
                height: 5,
                background: "var(--color-warms-300)",
                borderRadius: 999,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${pct}%`,
                  background: accent,
                  borderRadius: 999,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({
  segments,
  size = 160,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  size?: number;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  const r = size / 2 - 18;
  const cx = size / 2, cy = size / 2;
  let acc = 0;
  const circumference = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-warms-300)" strokeWidth={20} />
      {segments.map((s) => {
        const len = (s.value / total) * circumference;
        const dash = `${len} ${circumference - len}`;
        const offset = -((acc / total) * circumference) + circumference / 4; // start at top
        acc += s.value;
        return (
          <circle
            key={s.label}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={20}
            strokeDasharray={dash}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize={20} fontWeight={700} fill="var(--color-navy)">
        {Math.round(((segments[0]?.value ?? 0) / total) * 100)}%
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize={10.5} fill="var(--color-ink-muted)">
        {segments[0]?.label}
      </text>
    </svg>
  );
}

function MiniSparkline({ data, color, height = 40, fill }: { data: number[]; color: string; height?: number; fill?: boolean }) {
  const w = 200;
  const max = Math.max(...data), min = Math.min(...data);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${height - ((v - min) / (max - min || 1)) * height}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" style={{ width: "100%", height, display: "block" }}>
      {fill && (
        <polygon
          points={`0,${height} ${pts} ${w},${height}`}
          fill={color}
          opacity={0.18}
        />
      )}
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SectionCharts() {
  const stackedSeries = [
    [12, 14, 17, 22, 28, 33, 38, 42, 48, 55, 60, 65, 70, 76, 81, 85, 90],
    [8,  10, 13, 16, 19, 22, 26, 28, 32, 35, 38, 41, 44, 47, 50, 52, 55],
    [4,   5,  6,  8,  9, 11, 13, 14, 16, 17, 19, 20, 22, 24, 26, 28, 30],
  ];
  const stackedColors = ["var(--color-orange-700)", "var(--color-purple-400)", "var(--color-green-500)"];
  const carbonActions = [
    { label: "Stop investing in fossil fuels", value: "−9.1 tons", magnitude: 9.1 },
    { label: "Install solar panels",           value: "−6.9 tons", magnitude: 6.9 },
    { label: "Drive an EV",                     value: "−6.5 tons", magnitude: 6.5 },
    { label: "Avoid flying",                    value: "−3.6 tons", magnitude: 3.6 },
    { label: "Plant 100 trees",                  value: "−1.5 tons", magnitude: 1.5 },
    { label: "Go vegan",                          value: "−0.9 tons", magnitude: 0.9 },
    { label: "Don't use single use plastics",     value: "−0.4 tons", magnitude: 0.4 },
  ];
  return (
    <Section
      id="charts"
      title="Charts"
      description="The chart vocabulary used across Sphere products: stacked areas, magnitude bar lists, donuts, and sparklines. All series read from chart-1..chart-10 in the semantic-token map."
    >
      <div className="space-y-4">
        <Specimen label="Stacked Area · with tooltip indicator">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4 text-[11.5px]">
              <span className="inline-flex items-center gap-1.5">
                <span style={{ width: 8, height: 8, borderRadius: 999, background: stackedColors[0] }} />
                Total value
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span style={{ width: 8, height: 8, borderRadius: 999, background: stackedColors[1] }} />
                Earnings
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span style={{ width: 8, height: 8, borderRadius: 999, background: stackedColors[2] }} />
                Net investments
              </span>
            </div>
            <SegmentedControl options={["1M", "3M", "6M", "YTD", "1Y", "MAX"]} />
          </div>
          <StackedAreaChart series={stackedSeries} colors={stackedColors} />
        </Specimen>
        <Specimen label="Bar List · magnitude">
          <BarList items={carbonActions} accent="var(--color-purple-400)" />
        </Specimen>
        <Specimen label="Sparklines">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <SparkCard label="Account value" value="$985,389" series={[42, 48, 55, 62, 70, 80, 85, 92, 95, 100, 105, 112]} />
            <SparkCard label="Earnings" value="$79,356" series={[2, 6, 8, 15, 25, 32, 40, 50, 55, 68, 72, 79]} />
            <SparkCard label="Net contributions" value="$906k" series={[78, 81, 84, 87, 88, 89, 90, 90, 91, 91, 91, 91]} />
          </div>
        </Specimen>
        <Specimen label="Donut">
          <div className="flex flex-wrap items-center gap-8">
            <DonutChart
              segments={[
                { label: "Equities",   value: 62, color: "var(--color-navy)" },
                { label: "Bonds",      value: 24, color: "var(--color-sunshine)" },
                { label: "Cash",       value: 9,  color: "var(--color-purple-400)" },
                { label: "Other",      value: 5,  color: "var(--color-green-500)" },
              ]}
            />
            <div className="space-y-1.5 text-[12.5px]">
              <div className="flex items-center gap-2"><span style={{ width: 10, height: 10, background: "var(--color-navy)", borderRadius: 2 }} /> Equities · 62%</div>
              <div className="flex items-center gap-2"><span style={{ width: 10, height: 10, background: "var(--color-sunshine)", borderRadius: 2 }} /> Bonds · 24%</div>
              <div className="flex items-center gap-2"><span style={{ width: 10, height: 10, background: "var(--color-purple-400)", borderRadius: 2 }} /> Cash · 9%</div>
              <div className="flex items-center gap-2"><span style={{ width: 10, height: 10, background: "var(--color-green-500)", borderRadius: 2 }} /> Other · 5%</div>
            </div>
          </div>
        </Specimen>
      </div>
    </Section>
  );
}

// ─── Atmosphere-style helpers ───────────────────────────────────────────────

function HeroStat({
  value,
  delta,
  label,
  sub,
  helperIcon = true,
}: {
  value: string;
  delta?: { value: string; up?: boolean };
  label: string;
  sub?: string;
  helperIcon?: boolean;
}) {
  return (
    <div
      style={{
        background: "var(--color-warms-200)",
        border: "1px solid var(--color-rule)",
        borderRadius: 12,
        padding: 20,
        position: "relative",
      }}
    >
      {helperIcon && (
        <span
          style={{
            position: "absolute", top: 12, right: 12,
            width: 18, height: 18, borderRadius: 999,
            border: "1px solid var(--color-rule)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10,
            color: "var(--color-ink-muted)",
            cursor: "help",
          }}
        >?</span>
      )}
      <div className="mb-3 flex items-baseline gap-2">
        <span className="text-[26px] font-semibold tracking-tight" style={{ color: "var(--color-navy)", fontVariantNumeric: "tabular-nums" }}>{value}</span>
        {delta && (
          <span
            className="inline-flex items-center gap-0.5 text-[12.5px] font-semibold"
            style={{ color: delta.up ? "var(--color-green-700)" : "var(--color-red-700)" }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><polygon points={delta.up ? "5,1 9,9 1,9" : "5,9 9,1 1,1"} /></svg>
            {delta.value}
          </span>
        )}
      </div>
      <div className="text-[14px] font-semibold" style={{ color: "var(--color-navy)" }}>{label}</div>
      {sub && <div className="mt-0.5 text-[11.5px]" style={{ color: "var(--color-ink-muted)" }}>{sub}</div>}
    </div>
  );
}

function ActionCard({ title, body }: { title: string; body?: string }) {
  return (
    <button
      type="button"
      className="block w-full text-left transition"
      style={{
        background: "transparent",
        border: "1px solid var(--color-navy)",
        borderRadius: 12,
        padding: 16,
        cursor: "pointer",
        color: "var(--color-navy)",
        minHeight: 140,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div className="text-[14px] font-medium" style={{ lineHeight: 1.35 }}>{title}</div>
      {body && <div className="text-[12px]" style={{ color: "var(--color-warms-700)" }}>{body}</div>}
      <div className="mt-2 flex justify-end">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-navy)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14m0 0l-6-6m6 6l-6 6" />
        </svg>
      </div>
    </button>
  );
}

function EquivalentCard({
  number,
  label,
  imageEmoji,
}: {
  number: string;
  label: string;
  imageEmoji: string;
}) {
  return (
    <div
      style={{
        background: "var(--color-warms-200)",
        border: "1px solid var(--color-rule)",
        borderRadius: 12,
        padding: 16,
        position: "relative",
        overflow: "hidden",
        minHeight: 180,
      }}
    >
      <span style={{ position: "absolute", top: 10, right: 10, width: 18, height: 18, borderRadius: 999, border: "1px solid var(--color-rule)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "var(--color-ink-muted)" }}>?</span>
      <div className="flex items-baseline gap-2">
        <span className="text-[36px] font-semibold tracking-tight" style={{ color: "var(--color-navy)", fontVariantNumeric: "tabular-nums" }}>{number}</span>
        <span className="text-[13px] font-medium" style={{ color: "var(--color-navy)", maxWidth: 110, lineHeight: 1.2 }}>{label}</span>
      </div>
      <div style={{ position: "absolute", bottom: -8, left: 0, right: 0, textAlign: "center", fontSize: 86, lineHeight: 1, opacity: 0.85 }}>
        {imageEmoji}
      </div>
    </div>
  );
}

// ─── Layouts ────────────────────────────────────────────────────────────────

function LayoutFrame({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <Specimen label={label}>
      <div
        style={{
          background: "var(--color-warms-100)",
          border: "1px solid var(--color-rule)",
          borderRadius: 12,
          padding: 0,
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </Specimen>
  );
}

function LayoutPortfolio() {
  return (
    <LayoutFrame label="Portfolio · Atmosphere-style dashboard">
      <TopBar />
      <div className="px-6 py-6">
        <div className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="text-[13px] font-medium" style={{ color: "var(--color-navy)" }}>Good morning, Amy.</div>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="text-[40px] font-semibold tracking-tight" style={{ color: "var(--color-navy)", fontVariantNumeric: "tabular-nums" }}>$985,389</span>
              <span className="text-[16px]" style={{ color: "var(--color-navy)" }}>Account value</span>
            </div>
            <p className="mt-2 max-w-[60ch] text-[12.5px]" style={{ color: "var(--color-warms-700)" }}>
              Your $985k investments and $2.5k average monthly contributions have earned $79k since
              you first invested in Sphere funds on Jan 1, 2025.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11.5px]" style={{ color: "var(--color-warms-700)" }}>To transfer funds: Call 844.277.4373</span>
            <select style={{ ...fieldStyle("default"), width: 180 }}>
              <option>All accounts</option>
              <option>IRA · **17834</option>
              <option>Roth IRA · **22907</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-2">
          <HeroStat value="$79,356" delta={{ value: "10.32%", up: true }} label="Earnings" sub="Since Jan 1, 2025" />
          <HeroStat value="$906,306" label="Net investments" sub="Since Jan 1, 2025" />
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-4 text-[11.5px]">
            <span className="inline-flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--color-orange-700)" }} /> Total value</span>
            <span className="inline-flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--color-purple-400)" }} /> Earnings</span>
            <span className="inline-flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--color-green-500)" }} /> Net investments</span>
          </div>
          <SegmentedControl options={["1M", "3M", "6M", "YTD", "1Y", "MAX"]} />
        </div>
        <StackedAreaChart
          series={[
            [12, 14, 17, 22, 28, 33, 38, 42, 48, 55, 60, 65, 70, 76, 81, 85, 90],
            [8,  10, 13, 16, 19, 22, 26, 28, 32, 35, 38, 41, 44, 47, 50, 52, 55],
            [4,   5,  6,  8,  9, 11, 13, 14, 16, 17, 19, 20, 22, 24, 26, 28, 30],
          ]}
          colors={["var(--color-orange-700)", "var(--color-purple-400)", "var(--color-green-500)"]}
        />

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="mb-2 text-[14px] font-semibold" style={{ color: "var(--color-navy)" }}>Performance</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {[
                  { label: "Today", value: "$2,957",   pct: "0.3%",   up: true  },
                  { label: "1M",    value: "$8,136",   pct: "1.8%",   up: true  },
                  { label: "3M",    value: "-$818",     pct: "0.3%",   up: false },
                  { label: "6M",    value: "$50,903",  pct: "10.3%",  up: true  },
                  { label: "YTD",   value: "$13,290",  pct: "12.9%",  up: true  },
                  { label: "1Y",    value: "$58,739",  pct: "39.7%",  up: true  },
                  { label: "Max",   value: "$491,829", pct: "230.3%", up: true  },
                ].map((r, i, arr) => (
                  <tr key={r.label}>
                    <td style={{ padding: "8px 12px", fontSize: 12.5, color: "var(--color-navy)", borderBottom: i === arr.length - 1 ? undefined : "1px solid var(--color-rule)" }}>{r.label}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12.5, fontVariantNumeric: "tabular-nums", borderBottom: i === arr.length - 1 ? undefined : "1px solid var(--color-rule)" }}>
                      <span style={{ color: r.up ? "var(--color-green-700)" : "var(--color-red-700)" }}>▲ {r.value}</span>
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: r.up ? "var(--color-green-700)" : "var(--color-red-700)", borderBottom: i === arr.length - 1 ? undefined : "1px solid var(--color-rule)" }}>▲ {r.pct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="mb-2 text-[14px] font-semibold" style={{ color: "var(--color-navy)" }}>Transactions</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {[
                  { date: "Aug 1, 2025", kind: "Automated Contribution", amt: "$2,500",   pct: ".24%",  up: true  },
                  { date: "Jul 15, 2025", kind: "Contribution",           amt: "$15,000",  pct: "1.44%", up: true  },
                  { date: "Jul 1, 2025",  kind: "Contribution",           amt: "$2,500",   pct: ".25%",  up: true  },
                  { date: "Jun 23, 2025", kind: "Withdrawal",             amt: "-$15,000", pct: "1.54%", up: false },
                  { date: "Jun 1, 2025",  kind: "Automated Contribution", amt: "$2,500",   pct: ".26%",  up: true  },
                ].map((r, i, arr) => (
                  <tr key={r.date}>
                    <td style={{ padding: "8px 12px", fontSize: 12.5, color: "var(--color-warms-700)", borderBottom: i === arr.length - 1 ? undefined : "1px solid var(--color-rule)" }}>{r.date}</td>
                    <td style={{ padding: "8px 12px", fontSize: 12.5, color: "var(--color-navy)", borderBottom: i === arr.length - 1 ? undefined : "1px solid var(--color-rule)" }}>{r.kind}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: r.up ? "var(--color-green-700)" : "var(--color-red-700)", borderBottom: i === arr.length - 1 ? undefined : "1px solid var(--color-rule)" }}>▲ {r.amt}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: r.up ? "var(--color-green-700)" : "var(--color-red-700)", borderBottom: i === arr.length - 1 ? undefined : "1px solid var(--color-rule)" }}>▲ {r.pct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 flex justify-center">
              <DSButton variant="secondary" size="sm">Load more</DSButton>
            </div>
          </div>
        </div>
      </div>
    </LayoutFrame>
  );
}

function LayoutImpact() {
  const carbonActions = [
    { label: "Stop investing in fossil fuels", value: "−9.1 tons", magnitude: 9.1 },
    { label: "Install solar panels",           value: "−6.9 tons", magnitude: 6.9 },
    { label: "Drive an EV",                     value: "−6.5 tons", magnitude: 6.5 },
    { label: "Avoid flying",                    value: "−3.6 tons", magnitude: 3.6 },
    { label: "Plant 100 trees",                  value: "−1.5 tons", magnitude: 1.5 },
    { label: "Go vegan",                          value: "−0.9 tons", magnitude: 0.9 },
    { label: "Don't use single use plastics",     value: "−0.4 tons", magnitude: 0.4 },
  ];
  return (
    <LayoutFrame label="Impact · planet-scale figures">
      <TopBar />
      <div className="px-6 py-6">
        <div className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="text-[13px] font-medium" style={{ color: "var(--color-navy)" }}>Good morning, Amy.</div>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="text-[40px] font-semibold tracking-tight" style={{ color: "var(--color-navy)", fontVariantNumeric: "tabular-nums" }}>$985,389</span>
              <span className="text-[16px]" style={{ color: "var(--color-navy)" }}>Account value</span>
            </div>
            <p className="mt-2 max-w-[60ch] text-[12.5px]" style={{ color: "var(--color-warms-700)" }}>
              Your investments are making a real difference. Since Jan 1, you have taken $45k back
              from fossil fuel companies — equivalent to 90.1 tons reduced every year. That's more
              than not driving for 20 years.
            </p>
          </div>
          <div className="text-right text-[11.5px]" style={{ color: "var(--color-warms-700)" }}>As of Dec 3, 2025</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <HeroStat value="$45,418" label="Removed" sub="from fossil fuel investments" />
          <HeroStat value="−90.1 tons" label="Financed emissions" sub="Since Jan 1, 2025" />
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-4 text-[11.5px]">
                <span className="inline-flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--color-purple-400)" }} /> Removed from fossil fuels</span>
                <span className="inline-flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--color-green-500)" }} /> Financed emissions</span>
              </div>
            </div>
            <StackedAreaChart
              series={[
                [4, 6, 9, 12, 15, 18, 22, 26, 30, 34, 38, 42, 46, 50, 54, 58, 62],
                [3, 4, 6,  8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34],
              ]}
              colors={["var(--color-purple-400)", "var(--color-green-500)"]}
            />
            <div className="mt-4 flex items-center justify-between text-[12px]">
              <span style={{ color: "var(--color-navy)" }}>Action</span>
              <span style={{ color: "var(--color-ink-muted)" }}>CO₂ / year</span>
            </div>
            <div className="mt-2">
              <BarList items={carbonActions} accent="var(--color-purple-400)" />
            </div>
          </div>
          <div>
            <div className="mb-3 text-[14px] font-semibold" style={{ color: "var(--color-navy)" }}>Your divestment carbon reduction is equivalent to:</div>
            <div className="grid grid-cols-1 gap-3">
              <EquivalentCard number="20" label="years of driving" imageEmoji="🚗" />
              <EquivalentCard number="675" label="skipped flights" imageEmoji="✈️" />
              <EquivalentCard number="9,393" label="trees planted" imageEmoji="🌳" />
            </div>
          </div>
        </div>
        <div className="mt-6">
          <div className="mb-2 text-[14px] font-semibold" style={{ color: "var(--color-navy)" }}>More actions you can take</div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <ActionCard title="See how much your company invests in fossil fuel stocks." />
            <ActionCard title="Get your company to divest your 401(k) money." />
            <ActionCard title="Rally your friends and co-workers." />
            <ActionCard title="Email HR" />
          </div>
        </div>
      </div>
    </LayoutFrame>
  );
}

function LayoutOverview() {
  return (
    <LayoutFrame label="Overview · combined snapshot">
      <TopBar />
      <div className="px-6 py-6">
        <div className="mb-5">
          <div className="text-[13px] font-medium" style={{ color: "var(--color-navy)" }}>Good morning, Amy.</div>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="text-[40px] font-semibold tracking-tight" style={{ color: "var(--color-navy)", fontVariantNumeric: "tabular-nums" }}>$985,389</span>
            <span className="text-[16px]" style={{ color: "var(--color-navy)" }}>Account value</span>
          </div>
          <p className="mt-2 max-w-[64ch] text-[12.5px]" style={{ color: "var(--color-warms-700)" }}>
            Your investments are making a real impact. Since Jan 1, 2025, your $985k investments
            have earned $79k and reduced 90.1 tons of carbon every year you are divested.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <HeroStat value="$79,356"   delta={{ value: "▲", up: true }} label="Earnings"    sub="Since Jan 1, 2025" />
          <HeroStat value="$906,306"                                  label="Net investments" sub="Since Jan 1, 2025" />
          <HeroStat value="$45,418"                                   label="Removed"     sub="from fossil fuels" />
          <HeroStat value="−90.1 tons"                                label="Reduced"     sub="in financed emissions" />
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center gap-4 text-[11.5px]">
              <span className="inline-flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--color-orange-700)" }} /> Total value</span>
              <span className="inline-flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--color-purple-400)" }} /> Earnings</span>
              <span className="inline-flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--color-green-500)" }} /> Net investments</span>
            </div>
            <StackedAreaChart
              series={[
                [12, 14, 17, 22, 28, 33, 38, 42, 48, 55, 60, 65, 70, 76, 81, 85, 90],
                [8,  10, 13, 16, 19, 22, 26, 28, 32, 35, 38, 41, 44, 47, 50, 52, 55],
                [4,   5,  6,  8,  9, 11, 13, 14, 16, 17, 19, 20, 22, 24, 26, 28, 30],
              ]}
              colors={["var(--color-orange-700)", "var(--color-purple-400)", "var(--color-green-500)"]}
            />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between text-[12px]">
              <span style={{ color: "var(--color-navy)" }}>Action</span>
              <span style={{ color: "var(--color-ink-muted)" }}>CO₂ / year</span>
            </div>
            <BarList
              items={[
                { label: "Stop investing in fossil fuels", value: "−9.1 tons", magnitude: 9.1 },
                { label: "Install solar panels",           value: "−6.9 tons", magnitude: 6.9 },
                { label: "Drive an EV",                     value: "−6.5 tons", magnitude: 6.5 },
                { label: "Avoid flying",                    value: "−3.6 tons", magnitude: 3.6 },
                { label: "Plant 100 trees",                  value: "−1.5 tons", magnitude: 1.5 },
                { label: "Go vegan",                          value: "−0.9 tons", magnitude: 0.9 },
                { label: "Don't use single use plastics",     value: "−0.4 tons", magnitude: 0.4 },
              ]}
              accent="var(--color-purple-400)"
            />
          </div>
        </div>
        <div className="mt-6 mb-2 text-[14px] font-semibold" style={{ color: "var(--color-navy)" }}>
          Your financed emissions reduction is equivalent to
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <EquivalentCard number="20"    label="years of driving"  imageEmoji="🚗" />
          <EquivalentCard number="675"   label="skipped flights/year" imageEmoji="✈️" />
          <EquivalentCard number="9,393" label="trees planted"    imageEmoji="🌳" />
        </div>
      </div>
    </LayoutFrame>
  );
}

function LayoutAuth() {
  return (
    <LayoutFrame label="Auth · split-screen marketing + form">
      <div className="grid grid-cols-2" style={{ minHeight: 480 }}>
        <div
          style={{
            background:
              "linear-gradient(135deg, var(--color-navy) 0%, var(--color-cyan-700) 60%, var(--color-cyan-300) 100%)",
            color: "var(--color-warms-100)",
            padding: 48,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div className="text-[40px] font-semibold tracking-tight" style={{ lineHeight: 1.05, color: "var(--color-warms-100)" }}>
            Save the planet.<br />Save for yours.
          </div>
        </div>
        <div style={{ background: "var(--color-warms-100)", padding: 48, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <img src="/sphere-on-light.png" alt="Sphere" style={{ height: 40, width: "auto", marginBottom: 32 }} />
          <div className="mb-2 text-[13.5px] font-semibold" style={{ color: "var(--color-navy)" }}>
            Forgot your password? Please enter your email.
          </div>
          <DSInput placeholder="Email" />
          <div className="mt-3">
            <button
              type="button"
              className="w-full font-semibold"
              style={{
                background: "var(--color-sunshine)",
                color: "var(--color-navy)",
                border: "1px solid var(--color-sunshine)",
                borderRadius: 999,
                padding: "12px 18px",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Submit
            </button>
          </div>
          <div className="mt-4 text-[12px]" style={{ color: "var(--color-navy)" }}>
            Didn't get an email? <span style={{ color: "var(--color-sunshine)", cursor: "pointer" }}>Contact us.</span>
          </div>
          <div className="mt-12 text-right text-[11px]" style={{ color: "var(--color-sunshine)" }}>Terms of Use</div>
        </div>
      </div>
    </LayoutFrame>
  );
}

function LayoutMobile() {
  return (
    <Specimen label="Mobile · profile + bottom nav">
      <div className="flex flex-wrap items-start gap-6">
        <div
          style={{
            width: 320,
            background: "var(--color-warms-100)",
            border: "1px solid var(--color-rule)",
            borderRadius: 24,
            padding: 20,
            position: "relative",
          }}
        >
          <div className="mb-4 flex flex-col items-center gap-1.5">
            <Avatar size={72} src={AVATAR_BG_URL} badge="edit" />
            <button type="button" className="text-[12px] font-semibold" style={{ color: "var(--color-purple-500)", background: "transparent", border: "none", cursor: "pointer" }}>
              Delete photo
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <div className="mb-1 text-[12.5px] font-semibold" style={{ color: "var(--color-navy)" }}>Name</div>
              <DSInput defaultValue="Amy Chen" />
            </div>
            <div>
              <DSInput defaultValue="" placeholder="Last name" />
            </div>
            <div>
              <div className="mb-1 text-[12.5px] font-semibold" style={{ color: "var(--color-ink-muted)" }}>Email</div>
              <DSInput defaultValue="amy@oursphere.org" state="disabled" />
              <div className="mt-1 text-[11px]" style={{ color: "var(--color-warms-700)" }}>
                To change your password, contact us.
              </div>
            </div>
            <div>
              <div className="mb-1 text-[12.5px] font-semibold" style={{ color: "var(--color-navy)" }}>Current Password</div>
              <DSInput type="password" placeholder="Current Password" />
            </div>
            <div>
              <div className="mb-1 text-[12.5px] font-semibold" style={{ color: "var(--color-navy)" }}>New Password</div>
              <DSInput type="password" placeholder="New Password" />
            </div>
          </div>
          <div className="mt-4 flex justify-center">
            <BottomNav selected="profile" />
          </div>
        </div>
        <div
          style={{
            width: 320,
            background: "var(--color-warms-100)",
            border: "1px solid var(--color-rule)",
            borderRadius: 24,
            padding: 20,
          }}
        >
          <div className="mb-4 flex justify-center">
            <img src="/sphere-on-light.png" alt="Sphere" style={{ height: 30, width: "auto" }} />
          </div>
          <p className="text-[13px]" style={{ color: "var(--color-navy)" }}>
            Please share the details you used when setting up your Sphere account.
          </p>
          <div className="mt-4 space-y-3">
            <DSInput placeholder="Account Number" />
            <DSInput placeholder="Date of Birth" />
            <DSInput placeholder="Social Security Number" />
            <DSInput placeholder="Zip Code" />
          </div>
          <button
            type="button"
            className="mt-4 w-full font-semibold"
            style={{
              background: "var(--color-sunshine)",
              color: "var(--color-navy)",
              border: "1px solid var(--color-sunshine)",
              borderRadius: 999,
              padding: "12px 18px",
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Confirm
          </button>
          <div className="mt-4 text-[12.5px]" style={{ color: "var(--color-navy)" }}>
            <span className="font-semibold">Accounts already added:</span>
            <div className="mt-1 flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-green-700)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l5 5 9-11" />
              </svg>
              Amy Chen's IRA (**17834)
            </div>
          </div>
          <div className="mt-6 text-right text-[11px]" style={{ color: "var(--color-sunshine)" }}>Terms of Use</div>
        </div>
      </div>
    </Specimen>
  );
}

function SectionLayouts() {
  return (
    <Section
      id="layouts"
      title="Layouts"
      description="Composed views built from the primitives above. Each is a working preview built from the same semantic tokens; tweak a primitive and these reshape with it."
    >
      <div className="space-y-6">
        <LayoutAuth />
        <LayoutPortfolio />
        <LayoutImpact />
        <LayoutOverview />
        <LayoutMobile />
      </div>
    </Section>
  );
}

function Footer() {
  return (
    <footer className="pt-8 text-[11px] text-[color:var(--color-ink-muted)]">
      <p>
        Sphere Design System {VERSION}. Built and maintained at{" "}
        <a
          href="https://github.com/dougburnett/sphere-design-system"
          target="_blank"
          rel="noreferrer"
          className="underline-offset-2 hover:underline"
        >
          github.com/dougburnett/sphere-design-system
        </a>
        .
      </p>
    </footer>
  );
}
